<script lang="ts">
  /** True-3D city view (Three.js / WebGL).
   *  The procedural city (street grid, perimeter-blocks of building footprints, parks,
   *  harbour, parking lots) is rendered as REAL 3D geometry: extruded building meshes on
   *  a baked ground plane, lit by a directional "sun" that sweeps
   *  with the in-game clock (day ↔ night), an orthographic tilted "diorama" camera, and
   *  UnrealBloom so night windows + lights glow. Owned / for-sale buildings are glowing
   *  markers with floating HTML labels — click to enter or buy. Drag = pan, wheel = zoom,
   *  right-drag = rotate. The sim + Svelte UI are untouched. */
  import { onMount, onDestroy, createEventDispatcher } from 'svelte';
  import * as THREE from 'three';
  import { MapControls } from 'three/addons/controls/MapControls.js';
  import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
  import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
  import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
  import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
  import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
  import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
  import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
  import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
  import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
  import type { City, Building, CityPlot, BuildingArt } from '@sim/types';
  import { CITY_PLOTS, SIZE_FLOORS, districtMeta } from '@sim/config/plots';
  import { worldStore, dispatch } from '@sim/store';
  import { timeFrac } from '@sim/config';
  import { money } from '@sim/selectors';
  import { gfxQuality, PRESETS, AUTO_TARGET_LOW, AUTO_TARGET_HIGH, AUTO_MIN_SCALE, AUTO_MAX_SCALE, type GfxQuality } from './gfxSettings';

  const emit = createEventDispatcher();

  // When true (the player is inside a building), the canvas stays mounted but the render loop does
  // no work — avoids tearing down + rebuilding the whole 3D scene on every map↔building switch.
  export let paused = false;
  let clampDirty = true; // recompute the map-view clamp next frame (camera moved / resized / shown)
  // On un-pause (returning to the map), re-validate the camera bounds on the next frame.
  $: if (!paused) clampDirty = true;

  const WORLD = 2200;
  const OUTSKIRT = 1100;
  const OUTSKIRT_DETAIL = 700;
  const OUTER_SIZE = WORLD + OUTSKIRT * 2;
  const wpx = (pct: number) => (pct / 100) * WORLD;
  const SELL_PRICE = 30;
  const BATCH = 1.5;
  const PROPERTY_CLEARANCE = 58;

  // ---- ground palette (golden-hour; 3D lighting handles day↔night) ----
  const P = {
    ground: '#6f6657',
    pad: '#c6ba9f',
    padEdge: '#9d917b',
    park: '#7a9a4c',
    parkEdge: '#5a722f',
    tree: '#4c6531',
    treeLit: '#6f9248',
    curb: '#d7ccb3',
    asphalt: '#2f2c28',
    lotAsphalt: '#48443d',
    lane: '#e9c863',
    crosswalk: '#e7e0cd',
    water: '#3f7d99',
    waterDeep: '#2b5567',
    sand: '#cdb884',
  };
  const CARCOLORS = ['#dfe3e8', '#2b2f37', '#8a909a', '#b0494a', '#466fae', '#cab15a', '#3c6b4a'];
  // muted palette for parked-lot cars (the bright CARCOLORS over-saturated the lots)
  const LOTCAR = ['#a8aab0', '#54575e', '#7d736a', '#7a5556', '#566a86', '#8c8466', '#5d6f60', '#6a6d72'];
  // building materials (1920s-weathered). glass towers are the landmarks.
  const KINDS = [
    { wall: 0x8c4a3a, roof: 0x53312a, win: 0xffc77e, lo: 24, hi: 60, glass: false },
    { wall: 0xa7906c, roof: 0x6c5a40, win: 0xffe0a0, lo: 22, hi: 54, glass: false },
    { wall: 0x8d847a, roof: 0x5b534a, win: 0xd9d3c5, lo: 30, hi: 72, glass: false },
    { wall: 0x6c8aa0, roof: 0x394b58, win: 0xcdeaff, lo: 62, hi: 118, glass: true },
    { wall: 0x6f4a3b, roof: 0x432d25, win: 0xffb968, lo: 16, hi: 26, glass: false },
    { wall: 0xb3a085, roof: 0x796a50, win: 0xffe7b6, lo: 20, hi: 46, glass: false },
  ];

  // ---- helpers ----
  const rand = (a: number, b: number) => a + Math.random() * (b - a);
  function mulberry32(a: number) {
    return () => {
      a |= 0;
      a = (a + 0x6d2b79f5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function smoothstep(a: number, b: number, x: number) {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
    return t * t * (3 - 2 * t);
  }
  function nightFraction(f: number) {
    const dawn = 1 - smoothstep(0.18, 0.3, f);
    const dusk = smoothstep(0.72, 0.86, f);
    return Math.max(dawn, dusk);
  }
  const hexShade = (c: number, f: number) => {
    const r = Math.min(255, Math.max(0, (((c >> 16) & 255) * f) | 0));
    const g = Math.min(255, Math.max(0, (((c >> 8) & 255) * f) | 0));
    const b = Math.min(255, Math.max(0, ((c & 255) * f) | 0));
    return (r << 16) | (g << 8) | b;
  };
  const css = (c: number) => '#' + c.toString(16).padStart(6, '0');
  /** Scale a "#rrggbb" string's brightness by `f` — cheap per-pad tone jitter so identical pads
   *  don't tile as one flat, uniform slab. */
  function jitterHex(hex: string, f: number): string {
    const n = parseInt(hex.slice(1), 16);
    const r = Math.min(255, Math.max(0, Math.round(((n >> 16) & 255) * f)));
    const g = Math.min(255, Math.max(0, Math.round(((n >> 8) & 255) * f)));
    const b = Math.min(255, Math.max(0, Math.round((n & 255) * f)));
    return css((r << 16) | (g << 8) | b);
  }
  function propertyPosition(p: Pick<CityPlot, 'mapX' | 'mapY'>) {
    const nudge = (value: number, roads: number[]) => {
      let nearest = roads[0];
      for (const road of roads) if (Math.abs(value - road) < Math.abs(value - nearest)) nearest = road;
      const d = value - nearest;
      if (Math.abs(d) >= 82) return value;
      const side = d === 0 ? (value < WORLD / 2 ? -1 : 1) : Math.sign(d);
      return Math.min(WORLD - 90, Math.max(90, nearest + side * 88));
    };
    return { x: nudge(wpx(p.mapX), VX), z: nudge(wpx(p.mapY), VY) };
  }

  // ---- street grid (irregular arterials; every-other line a wide avenue) ----
  const colF = [0.12, 0.26, 0.42, 0.58, 0.73, 0.88];
  const rowF = [0.13, 0.28, 0.44, 0.6, 0.75, 0.9];
  const VX = colF.map((f) => f * WORLD);
  const VY = rowF.map((f) => f * WORLD);
  type Seg = { x1: number; y1: number; x2: number; y2: number; horiz: boolean; major: boolean };
  const segs: Seg[] = [];
  VX.forEach((x, i) => segs.push({ x1: x, y1: 0, x2: x, y2: WORLD, horiz: false, major: i % 2 === 0 }));
  VY.forEach((y, i) => segs.push({ x1: 0, y1: y, x2: WORLD, y2: y, horiz: true, major: i % 2 === 0 }));
  function roadPoint() {
    const s = segs[(Math.random() * segs.length) | 0];
    let t = Math.random();
    // keep pedestrians / dealers on dry land — never out on the harbour
    if (!s.horiz) t = Math.min(t, WATER_T * 0.97);
    return { x: s.x1 + (s.x2 - s.x1) * t, y: s.y1 + (s.y2 - s.y1) * t };
  }

  const GX = [0, ...VX, WORLD];
  const GY = [0, ...VY, WORLD];
  // Harbour occupies the south row; the major avenues bridge across it.
  const WATER_Z0 = GY[GY.length - 2];
  const WATER_T = WATER_Z0 / WORLD;
  // Keep car bodies off the harbour: the shoreline avenue sits right at the water edge, so a
  // non-bridge car's south lane / turn-back point must stop north of the water plane (car
  // half-length is ~7, so leave a 10-unit margin).
  const CAR_SHORE_Z = WATER_Z0 - 10;
  const CAR_SHORE_T = CAR_SHORE_Z / WORLD;
  const BRIDGE_X = VX.filter((_, i) => i % 2 === 0);
  const BRIDGE_DECK_Y = 6;
  type Block = { x: number; y: number; w: number; h: number; type: 'build' | 'park' | 'water' | 'lot' };
  type Foot = { x: number; y: number; w: number; h: number; height: number; kind: number; tone: number; zone: string };
  type TreePos = { x: number; z: number; s: number };
  const blocks: Block[] = [];
  const footprints: Foot[] = [];
  const parkTrees: TreePos[] = [];
  const streetTrees: TreePos[] = [];
  // small greenery tucked into the pad "collar" around ordinary building lots (the gap between
  // FOOT_M-inset footprints and the PAD_INSET-inset pad) — breaks up bare pad on non-park blocks.
  const padTrees: TreePos[] = [];
  // Real open middles (from the rowRun/colRun perimeter layout) big enough to treat as a proper
  // pocket-park patch with a crossing walking path, instead of scattering bare pad with dots.
  type Courtyard = { x: number; y: number; w: number; h: number; path: 'h' | 'v' | 'cross' };
  const courtyards: Courtyard[] = [];
  const PAD_INSET = 16;
  const FOOT_M = 24;
  (() => {
    const rnd = mulberry32(0x1234abcd);
    const rows = GY.length - 1;
    const cols = GX.length - 1;
    // each neighbourhood has a distinct material mix + height profile
    const KIND_BAGS: Record<string, number[]> = {
      docks: [4, 4, 4, 2],
      downtown: [2, 2, 3, 1, 0],
      heights: [1, 1, 2, 3, 5],
      residential: [0, 0, 0, 5, 5, 1],
    };
    const HEIGHT_MUL: Record<string, number> = { docks: 0.76, downtown: 1.08, heights: 1.0, residential: 0.88 };
    const zoneAt = (i: number, j: number) => {
      if (j >= rows - 2) return 'docks';
      if (i >= 2 && i <= 4 && j >= 1 && j <= 3) return 'downtown';
      if (j === 0) return 'heights';
      return 'residential';
    };
    const pickKind = (zone: string) => {
      const bag = KIND_BAGS[zone] ?? KIND_BAGS.residential;
      return bag[(rnd() * bag.length) | 0];
    };
    const overlapsProperty = (x: number, y: number, w: number, h: number) =>
      CITY_PLOTS.some((p) => {
        const pp = propertyPosition(p);
        const px = pp.x;
        const pz = pp.z;
        return px > x - PROPERTY_CLEARANCE && px < x + w + PROPERTY_CLEARANCE && pz > y - PROPERTY_CLEARANCE && pz < y + h + PROPERTY_CLEARANCE;
      });
    const push = (x: number, y: number, w: number, h: number, kind: number, zone: string, mul = 1, lm = false) => {
      if (overlapsProperty(x, y, w, h)) return;
      const k = KINDS[kind];
      const height = lm ? 76 + rnd() * 52 : (k.lo + rnd() * (k.hi - k.lo)) * mul;
      footprints.push({ x, y, w, h, height, kind, tone: 0.85 + rnd() * 0.3, zone });
    };
    const GAP = 7;
    const rowRun = (x0: number, y0: number, len: number, depth: number, zone: string, mul: number) => {
      for (let p = x0; p < x0 + len - 24; ) {
        const seg = Math.min(58 + rnd() * 72, x0 + len - p);
        if (seg < 30) break;
        push(p, y0, seg - GAP, depth, pickKind(zone), zone, mul);
        p += seg;
      }
    };
    const colRun = (x0: number, y0: number, len: number, depth: number, zone: string, mul: number) => {
      for (let p = y0; p < y0 + len - 24; ) {
        const seg = Math.min(58 + rnd() * 72, y0 + len - p);
        if (seg < 30) break;
        push(x0, p, depth, seg - GAP, pickKind(zone), zone, mul);
        p += seg;
      }
    };
    for (let j = 0; j < rows; j++)
      for (let i = 0; i < cols; i++) {
        const x = GX[i],
          y = GY[j],
          w = GX[i + 1] - GX[i],
          h = GY[j + 1] - GY[j];
        let type: Block['type'] = 'build';
        if (j === rows - 1) type = 'water';
        else if ((i + j) % 5 === 0 && rnd() < 0.5) type = 'park';
        else if ((i * 3 + j) % 7 === 2) type = 'lot';
        blocks.push({ x, y, w, h, type });
        if (type === 'park') {
          const px = x + PAD_INSET + 10,
            py = y + PAD_INSET + 10,
            pw = w - 2 * (PAD_INSET + 10),
            ph = h - 2 * (PAD_INSET + 10);
          if (pw > 20 && ph > 20) {
            const n = 10 + ((rnd() * 10) | 0);
            for (let t = 0; t < n; t++) parkTrees.push({ x: px + rnd() * pw, z: py + rnd() * ph, s: 0.9 + rnd() * 0.6 });
          }
          continue;
        }
        if (type !== 'build') continue;

        const zone = zoneAt(i, j);
        const mul = HEIGHT_MUL[zone] ?? 1;
        const bx = x + FOOT_M,
          by = y + FOOT_M,
          bw = w - FOOT_M * 2,
          bh = h - FOOT_M * 2;
        if (bw < 44 || bh < 44) continue;
        // Slimmer landmarks are scattered across the city instead of forming a
        // single oversized downtown wall.
        const landmarkChance = zone === 'downtown' ? 0.14 : zone === 'heights' ? 0.11 : zone === 'residential' ? 0.055 : 0.025;
        const depth = Math.min(Math.max(Math.min(bw, bh) * 0.34, 44), 82);
        const footStart = footprints.length;
        let courtyard: Courtyard | null = null;
        if (rnd() < landmarkChance) {
          const tw = bw * (0.42 + rnd() * 0.16);
          const th = bh * (0.42 + rnd() * 0.16);
          push(bx + (bw - tw) / 2, by + (bh - th) / 2, tw, th, 3, zone, 1, true);
          // A landmark is deliberately smaller than its lot, so it can leave a *big* bare ring —
          // the biggest, most visible empty spaces in the city, not the small collars elsewhere.
          // Treat it as a landscaped plaza: inset from the pad edge (so a sidewalk strip stays
          // bare, not wall-to-wall green) and only when the ring is actually substantial.
          if (1 - (tw * th) / (bw * bh) > 0.42) {
            const m = 20;
            const roll = rnd();
            const path: Courtyard['path'] = roll < 0.4 ? 'cross' : roll < 0.7 ? 'h' : 'v';
            courtyard = { x: bx + m, y: by + m, w: bw - 2 * m, h: bh - 2 * m, path };
            courtyards.push(courtyard);
          }
        } else if (bw < depth * 2.3 || bh < depth * 2.3) {
          push(bx, by, bw, bh, pickKind(zone), zone, mul);
        } else {
          rowRun(bx, by, bw, depth, zone, mul);
          rowRun(bx, by + bh - depth, bw, depth, zone, mul);
          const midY = by + depth + GAP;
          const midH = bh - 2 * (depth + GAP);
          if (midH > 30) {
            colRun(bx, midY, midH, depth, zone, mul);
            colRun(bx + bw - depth, midY, midH, depth, zone, mul);
            // the space the perimeter runs leave in the middle — a real courtyard, not just a collar
            const cw = bw - 2 * (depth + GAP);
            if (cw > 34 && midH > 34) {
              const roll = rnd();
              const path: Courtyard['path'] = roll < 0.4 ? 'cross' : roll < 0.7 ? 'h' : 'v';
              courtyard = { x: bx + depth + GAP, y: midY, w: cw, h: midH, path };
              courtyards.push(courtyard);
            }
          }
        }
        const blockFoots = footprints.slice(footStart);
        if (courtyard) {
          // Pocket-park courtyard (painted as one patch in drawGround): populate with trees, kept
          // clear of the crossing path and (for the landmark-ring case) the building itself, and
          // capped so a big lot reads as a landscaped plaza, not a planted forest.
          const cy = courtyard;
          const inset = 6;
          const pathHalf = 7;
          const clear = 9;
          const midX = cy.x + cy.w / 2,
            midZ = cy.y + cy.h / 2;
          const footArea = blockFoots.reduce((s, f) => s + f.w * f.h, 0);
          const usable = Math.max(0, cy.w * cy.h - footArea);
          const wantTrees = Math.min(11, Math.max(2, Math.round(usable / 2400)));
          let placed = 0;
          for (let t = 0; t < wantTrees * 6 && placed < wantTrees; t++) {
            const tx = cy.x + inset + rnd() * Math.max(1, cy.w - inset * 2);
            const tz = cy.y + inset + rnd() * Math.max(1, cy.h - inset * 2);
            const onPathH = (cy.path === 'h' || cy.path === 'cross') && Math.abs(tz - midZ) < pathHalf;
            const onPathV = (cy.path === 'v' || cy.path === 'cross') && Math.abs(tx - midX) < pathHalf;
            if (onPathH || onPathV) continue;
            const hits = blockFoots.some((f) => tx > f.x - clear && tx < f.x + f.w + clear && tz > f.y - clear && tz < f.y + f.h + clear);
            if (hits) continue;
            padTrees.push({ x: tx, z: tz, s: 0.5 + rnd() * 0.3 });
            placed++;
          }
        } else {
          // Pad-collar greenery: tree count scales with how much of the block's pad is actually
          // empty (a tight single-building lot gets a token tree or none), rejected if a candidate
          // spot would land inside one of this block's own footprints.
          const padArea = Math.max(1, (w - 2 * (PAD_INSET + 10)) * (h - 2 * (PAD_INSET + 10)));
          const footArea = blockFoots.reduce((s, f) => s + f.w * f.h, 0);
          const emptyFrac = Math.max(0, Math.min(1, 1 - footArea / padArea));
          let wantTrees = Math.round(emptyFrac * 7 * (0.55 + rnd() * 0.7));
          // a real gap should never read as one lonely tree
          if (wantTrees === 1 && emptyFrac > 0.25) wantTrees = 2;
          const clear = 9;
          let placed = 0;
          for (let t = 0; t < wantTrees * 6 && placed < wantTrees; t++) {
            const tx = x + PAD_INSET + 10 + rnd() * Math.max(1, w - 2 * (PAD_INSET + 10));
            const tz = y + PAD_INSET + 10 + rnd() * Math.max(1, h - 2 * (PAD_INSET + 10));
            const hits = blockFoots.some((f) => tx > f.x - clear && tx < f.x + f.w + clear && tz > f.y - clear && tz < f.y + f.h + clear);
            if (hits) continue;
            padTrees.push({ x: tx, z: tz, s: 0.45 + rnd() * 0.25 });
            placed++;
          }
        }
      }
    // street trees lining the avenues (on the sidewalk side of major roads)
    for (const s of segs) {
      if (!s.major) continue;
      const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
      const ux = (s.x2 - s.x1) / len,
        uy = (s.y2 - s.y1) / len;
      const px = -uy,
        py = ux;
      const off = 25;
      for (let d = 60; d < len - 60; d += 54) {
        for (const side of [-1, 1]) {
          if (rnd() < 0.45) continue;
          const tx = s.x1 + ux * d + px * off * side;
          const tz = s.y1 + uy * d + py * off * side;
          if (!CITY_PLOTS.some((p) => {
            const pp = propertyPosition(p);
            return Math.hypot(tx - pp.x, tz - pp.z) < PROPERTY_CLEARANCE;
          }))
            streetTrees.push({ x: tx, z: tz, s: 0.55 + rnd() * 0.25 });
        }
      }
    }
  })();

  // Visual-only city rings use the same materials and instanced skyline as the
  // playable core. They exist solely to remove the visible square-map seam.
  type OuterBlock = { x: number; y: number; w: number; h: number; type: 'build' | 'park' | 'lot' | 'industrial' };
  const outerBlocks: OuterBlock[] = [];
  const outerFootprints: Foot[] = [];
  const outerTrees: TreePos[] = [];
  const extendAxis = (inner: number[]) => {
    const axis = [-OUTSKIRT];
    for (let v = -OUTSKIRT + 280; v < 0; v += 280) axis.push(v);
    axis.push(0, ...inner, WORLD);
    for (let v = WORLD + 280; v < WORLD + OUTSKIRT; v += 280) axis.push(v);
    axis.push(WORLD + OUTSKIRT);
    return [...new Set(axis)].sort((a, b) => a - b);
  };
  const OX = extendAxis(VX);
  const OZ = extendAxis(VY);
  (() => {
    const rnd = mulberry32(0x51de7a11);
    const kinds = [0, 1, 4, 4, 5, 5, 2, 4];
    const add = (x: number, y: number, w: number, d: number, forced = -1) => {
      const kind = forced >= 0 ? forced : kinds[(rnd() * kinds.length) | 0];
      const k = KINDS[kind];
      // Visual-only background: low, squat massing that never competes with the core skyline.
      outerFootprints.push({
        x,
        y,
        w,
        h: d,
        height: Math.max(9, Math.min(22, (k.lo + rnd() * (k.hi - k.lo)) * 0.4)),
        kind,
        tone: 0.72 + rnd() * 0.22,
        zone: 'outer',
      });
    };
    for (let j = 0; j < OZ.length - 1; j++) {
      for (let i = 0; i < OX.length - 1; i++) {
        const x = OX[i];
        const y = OZ[j];
        const w = OX[i + 1] - x;
        const h = OZ[j + 1] - y;
        const cx = x + w / 2;
        const cz = y + h / 2;
        if (cx > 0 && cx < WORLD && cz > 0 && cz < WORLD) continue;
        // More parks / lots / industrial land breaks the uniform residential repetition.
        const roll = rnd();
        const type: OuterBlock['type'] = roll < 0.17 ? 'park' : roll < 0.31 ? 'lot' : roll < 0.42 ? 'industrial' : 'build';
        outerBlocks.push({ x, y, w, h, type });
        if (type === 'park') {
          const count = 9 + ((rnd() * 10) | 0);
          for (let t = 0; t < count; t++) outerTrees.push({ x: x + 28 + rnd() * Math.max(1, w - 56), z: y + 28 + rnd() * Math.max(1, h - 56), s: 0.55 + rnd() * 0.5 });
          continue;
        }
        if (type === 'lot') continue;
        const inset = 23;
        const bx = x + inset;
        const bz = y + inset;
        const bw = w - inset * 2;
        const bh = h - inset * 2;
        if (bw < 50 || bh < 50) continue;
        if (type === 'industrial') {
          // One large flat shed fills the block — a clear change of grain from housing.
          add(bx, bz, bw, bh, rnd() < 0.5 ? 4 : 2);
          continue;
        }
        const cols = bw > 230 ? 3 : 2;
        const rows = bh > 230 ? 3 : 2;
        const gap = 8;
        const cw = (bw - gap * (cols - 1)) / cols;
        const ch = (bh - gap * (rows - 1)) / rows;
        for (let ry = 0; ry < rows; ry++)
          for (let rx = 0; rx < cols; rx++) {
            if (rnd() < 0.42) continue;
            const pad = 5 + rnd() * 5;
            add(bx + rx * (cw + gap) + pad, bz + ry * (ch + gap) + pad, cw - pad * 2, ch - pad * 2);
          }
      }
    }
  })();

  const districts = [
    { t: 'THE HEIGHTS', x: 50, y: 8 },
    { t: 'SOUTHGATE', x: 84, y: 18 },
    { t: 'MIDTOWN', x: 86, y: 50 },
    { t: 'RIVERSIDE', x: 58, y: 52 },
    { t: 'OLD TOWN', x: 80, y: 74 },
    { t: 'EASTSIDE', x: 16, y: 70 },
    { t: 'THE STRIP', x: 14, y: 34 },
    { t: 'THE DOCKS', x: 44, y: 95 },
  ];

  // ---- ground texture (baked Canvas2D; 3D lighting renders day↔night) ----
  function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, r);
  }
  function drawGround(): HTMLCanvasElement {
    const S = 2048;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d')!;
    const k = S / WORLD;
    const trnd = mulberry32(0x7e57ca);
    ctx.fillStyle = P.ground;
    ctx.fillRect(0, 0, S, S);
    // Neighbourhood colour washes make each area feel authored without obscuring roads.
    const districtTints = ['rgba(101,132,154,.12)', 'rgba(153,91,72,.11)', 'rgba(133,112,167,.10)', 'rgba(78,132,126,.11)', 'rgba(157,119,67,.10)', 'rgba(132,83,69,.11)', 'rgba(160,75,112,.09)', 'rgba(54,105,121,.13)'];
    districts.forEach((d, i) => {
      const x = wpx(d.x) * k;
      const y = wpx(d.y) * k;
      const r = 360 * k;
      const glow = ctx.createRadialGradient(x, y, 0, x, y, r);
      glow.addColorStop(0, districtTints[i]);
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    });
    // pads / parks / water / lots
    for (const bl of blocks) {
      const sx = bl.x * k,
        sy = bl.y * k,
        sw = bl.w * k,
        sh = bl.h * k;
      if (bl.type === 'water') {
        ctx.fillStyle = P.sand;
        roundRectPath(ctx, sx - 2, sy - 2, sw + 4, sh + 4, 8 * k);
        ctx.fill();
        const grd = ctx.createLinearGradient(0, sy, 0, sy + sh);
        grd.addColorStop(0, P.water);
        grd.addColorStop(1, P.waterDeep);
        ctx.fillStyle = grd;
        roundRectPath(ctx, sx + 7 * k, sy + 7 * k, sw - 14 * k, sh - 14 * k, 8 * k);
        ctx.fill();
        ctx.fillStyle = 'rgba(255,255,255,0.18)';
        for (let i = 0; i < 7; i++) ctx.fillRect(sx + (16 + trnd() * (bl.w - 60)) * k, sy + (16 + trnd() * (bl.h - 30)) * k, (24 + trnd() * 44) * k, 1.5 * k);
        continue;
      }
      const px = sx + PAD_INSET * k,
        py = sy + PAD_INSET * k,
        pw = sw - PAD_INSET * 2 * k,
        ph = sh - PAD_INSET * 2 * k;
      if (pw < 12 || ph < 12) continue;
      if (bl.type === 'park') {
        ctx.fillStyle = P.parkEdge;
        roundRectPath(ctx, px, py, pw, ph, 16 * k);
        ctx.fill();
        ctx.fillStyle = P.park;
        roundRectPath(ctx, px + 4 * k, py + 4 * k, pw - 8 * k, ph - 8 * k, 13 * k);
        ctx.fill();
        // 3D trees are placed separately (see parkTrees)
        continue;
      }
      // build / lot pad
      ctx.fillStyle = P.padEdge;
      roundRectPath(ctx, px, py, pw, ph, 13 * k);
      ctx.fill();
      // slight per-pad tone jitter — otherwise every lot is the exact same flat tan card
      ctx.fillStyle = jitterHex(P.pad, 0.93 + trnd() * 0.14);
      roundRectPath(ctx, px + 3 * k, py + 3 * k, pw - 6 * k, ph - 6 * k, 11 * k);
      ctx.fill();
      // a faint expansion-joint seam breaks up the bare pad (skipped ~30% of the time, and never
      // on lots, which get their own asphalt below) — one line only, so it reads as texture, not a grid
      if (bl.type !== 'lot' && pw > 22 * k && ph > 22 * k && trnd() < 0.7) {
        ctx.strokeStyle = 'rgba(120,109,84,0.2)';
        ctx.lineWidth = Math.max(1, k);
        ctx.beginPath();
        if (trnd() < 0.5) {
          const lx = px + pw * (0.32 + trnd() * 0.36);
          ctx.moveTo(lx, py + 5 * k);
          ctx.lineTo(lx, py + ph - 5 * k);
        } else {
          const ly = py + ph * (0.32 + trnd() * 0.36);
          ctx.moveTo(px + 5 * k, ly);
          ctx.lineTo(px + pw - 5 * k, ly);
        }
        ctx.stroke();
      }
      if (bl.type === 'lot') {
        const lx = px + 10 * k,
          ly = py + 10 * k,
          lw = pw - 20 * k,
          lh = ph - 20 * k;
        ctx.fillStyle = P.lotAsphalt;
        roundRectPath(ctx, lx, ly, lw, lh, 7 * k);
        ctx.fill();
        const bay = 19 * k;
        for (let ry = ly + 14 * k; ry < ly + lh - 22 * k; ry += 40 * k) {
          ctx.fillStyle = 'rgba(233,200,99,0.5)';
          for (let s = lx + 8 * k; s < lx + lw - 8 * k; s += bay) ctx.fillRect(s, ry, 1.5 * k, 30 * k);
          for (let s = lx + 9 * k; s < lx + lw - bay; s += bay) {
            if (trnd() < 0.5) continue;
            const top = trnd() < 0.5;
            ctx.fillStyle = LOTCAR[(trnd() * LOTCAR.length) | 0];
            roundRectPath(ctx, s + 1.5 * k, top ? ry + 1 * k : ry + 15 * k, bay - 4 * k, 13 * k, 2 * k);
            ctx.fill();
          }
        }
      }
    }
    // Soft contact-shadow hugging each footprint — a cheap baked complement to the live GTAO pass
    // (GTAO alone reads too subtly on a flat, uniformly-lit pad from this tilted-down height). The
    // footprint itself is always covered by the actual 3D building mesh, so bleed in both
    // directions is fine — only the outward bleed onto the surrounding pad is ever visible.
    ctx.save();
    ctx.filter = `blur(${Math.max(2, 5 * k)}px)`;
    ctx.fillStyle = 'rgba(24,20,14,0.32)';
    for (const f of footprints) {
      roundRectPath(ctx, f.x * k, f.y * k, f.w * k, f.h * k, 4 * k);
      ctx.fill();
    }
    ctx.restore();
    // Courtyard pocket-parks: the rowRun/colRun perimeter layout's real open middles get a proper
    // sized-to-fit green patch (same styling as a dedicated park block) plus a crossing walking
    // path in the sidewalk-curb colour, connecting toward the surrounding buildings.
    for (const yard of courtyards) {
      const px = yard.x * k,
        py = yard.y * k,
        pw = yard.w * k,
        ph = yard.h * k;
      ctx.fillStyle = P.parkEdge;
      roundRectPath(ctx, px, py, pw, ph, 14 * k);
      ctx.fill();
      ctx.fillStyle = P.park;
      roundRectPath(ctx, px + 5 * k, py + 5 * k, pw - 10 * k, ph - 10 * k, 11 * k);
      ctx.fill();
      const pathW = 9 * k;
      const overrun = 7 * k;
      ctx.fillStyle = P.curb;
      if (yard.path === 'h' || yard.path === 'cross') {
        ctx.fillRect(px - overrun, py + ph / 2 - pathW / 2, pw + overrun * 2, pathW);
      }
      if (yard.path === 'v' || yard.path === 'cross') {
        ctx.fillRect(px + pw / 2 - pathW / 2, py - overrun, pathW, ph + overrun * 2);
      }
    }
    // Small planting bed under pad trees not already inside a courtyard patch above — otherwise
    // it's a trunk standing directly on bare pavement. Same styling as a park block, just circular.
    for (const t of padTrees) {
      const inYard = courtyards.some((yard) => t.x > yard.x && t.x < yard.x + yard.w && t.z > yard.y && t.z < yard.y + yard.h);
      if (inYard) continue;
      const r = 8 * t.s * k;
      const cx = t.x * k,
        cz = t.z * k;
      ctx.fillStyle = P.parkEdge;
      ctx.beginPath();
      ctx.arc(cx, cz, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = P.park;
      ctx.beginPath();
      ctx.arc(cx, cz, r * 0.76, 0, Math.PI * 2);
      ctx.fill();
    }
    // roads: casing → asphalt → dashed lanes → crosswalks
    ctx.lineCap = 'round';
    // Vertical roads stop at the waterfront (bridges carry the avenues across in 3D).
    const eY = (s: Seg) => (s.horiz ? s.y2 : Math.min(s.y2, WATER_Z0));
    for (const s of segs) {
      ctx.strokeStyle = P.curb;
      ctx.lineWidth = (s.major ? 34 : 22) * k;
      ctx.beginPath();
      ctx.moveTo(s.x1 * k, s.y1 * k);
      ctx.lineTo(s.x2 * k, eY(s) * k);
      ctx.stroke();
    }
    for (const s of segs) {
      ctx.strokeStyle = P.asphalt;
      ctx.lineWidth = (s.major ? 26 : 15) * k;
      ctx.beginPath();
      ctx.moveTo(s.x1 * k, s.y1 * k);
      ctx.lineTo(s.x2 * k, eY(s) * k);
      ctx.stroke();
    }
    ctx.strokeStyle = P.lane;
    ctx.lineWidth = 2.4 * k;
    ctx.setLineDash([16 * k, 16 * k]);
    for (const s of segs)
      if (s.major) {
        ctx.beginPath();
        ctx.moveTo(s.x1 * k, s.y1 * k);
        ctx.lineTo(s.x2 * k, eY(s) * k);
        ctx.stroke();
      }
    ctx.setLineDash([]);
    ctx.fillStyle = 'rgba(231,224,205,0.5)';
    const cw = (cx: number, cy: number, horiz: boolean) => {
      for (let n = 0; n < 5; n++) {
        const o = (n - 2) * 5 * k;
        if (horiz) ctx.fillRect(cx - 16 * k, cy + o - 1.5 * k, 32 * k, 3 * k);
        else ctx.fillRect(cx + o - 1.5 * k, cy - 16 * k, 3 * k, 32 * k);
      }
    };
    VX.forEach((x, ix) =>
      VY.forEach((y, iy) => {
        if (ix % 2 === 0 && iy % 2 === 0) {
          cw(x * k, (y - 22) * k, true);
          cw(x * k, (y + 22) * k, true);
          cw((x - 22) * k, y * k, false);
          cw((x + 22) * k, y * k, false);
        }
      })
    );
    // Patched asphalt, utility markings and short alley seams break up the pristine grid.
    ctx.lineCap = 'butt';
    for (let i = 0; i < 90; i++) {
      const s = segs[(trnd() * segs.length) | 0];
      const t = 0.04 + trnd() * 0.92;
      const x = (s.x1 + (s.x2 - s.x1) * t) * k;
      const y = (s.y1 + (s.y2 - s.y1) * t) * k;
      ctx.strokeStyle = trnd() < 0.18 ? 'rgba(65,142,181,.32)' : 'rgba(12,13,14,.22)';
      ctx.lineWidth = (0.8 + trnd() * 1.4) * k;
      ctx.beginPath();
      ctx.moveTo(x - (4 + trnd() * 8) * k, y);
      ctx.lineTo(x + (5 + trnd() * 12) * k, y + (trnd() - 0.5) * 7 * k);
      ctx.stroke();
    }
    return cv;
  }
  function drawGroundGlow(): HTMLCanvasElement {
    // emissive map: warm streetlight pools along the avenues (glows at night)
    const S = 1024;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d')!;
    const k = S / WORLD;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, S, S);
    // Faint warm sheen down every avenue so the core streets stay legible at night.
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'rgba(255,176,96,0.16)';
    for (const s of segs) {
      if (!s.major) continue;
      ctx.lineWidth = 20 * k;
      ctx.beginPath();
      ctx.moveTo(s.x1 * k, s.y1 * k);
      ctx.lineTo(s.x2 * k, s.y2 * k);
      ctx.stroke();
    }
    for (const s of segs) {
      if (!s.major) continue;
      const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
      const ux = (s.x2 - s.x1) / len,
        uy = (s.y2 - s.y1) / len;
      for (let d = 70; d < len; d += 150) {
        const lx = (s.x1 + ux * d) * k,
          ly = (s.y1 + uy * d) * k;
        const r = 26 * k;
        const g = ctx.createRadialGradient(lx, ly, 0, lx, ly, r);
        g.addColorStop(0, 'rgba(255,197,121,0.95)');
        g.addColorStop(0.5, 'rgba(255,170,90,0.4)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(lx, ly, r, 0, 7);
        ctx.fill();
      }
    }
    return cv;
  }

  function drawOutskirts(): HTMLCanvasElement {
    const S = 1536;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d')!;
    const k = S / OUTER_SIZE;
    const wrnd = mulberry32(0x0f75c17e);
    const wp = (v: number) => (v + OUTSKIRT) * k;
    ctx.fillStyle = P.ground;
    ctx.fillRect(0, 0, S, S);
    for (const bl of outerBlocks) {
      const px = wp(bl.x + PAD_INSET);
      const py = wp(bl.y + PAD_INSET);
      const pw = (bl.w - PAD_INSET * 2) * k;
      const ph = (bl.h - PAD_INSET * 2) * k;
      if (pw <= 2 || ph <= 2) continue;
      ctx.fillStyle = bl.type === 'park' ? P.parkEdge : P.padEdge;
      ctx.fillRect(px, py, pw, ph);
      ctx.fillStyle = bl.type === 'park' ? P.park : P.pad;
      ctx.fillRect(px + 4 * k, py + 4 * k, pw - 8 * k, ph - 8 * k);
      if (bl.type === 'lot') {
        ctx.fillStyle = P.lotAsphalt;
        ctx.fillRect(px + 12 * k, py + 12 * k, pw - 24 * k, ph - 24 * k);
        ctx.strokeStyle = 'rgba(233,200,99,.46)';
        ctx.lineWidth = 1.4 * k;
        for (let x = px + 20 * k; x < px + pw - 18 * k; x += 24 * k) {
          ctx.beginPath();
          ctx.moveTo(x, py + 18 * k);
          ctx.lineTo(x, py + ph - 18 * k);
          ctx.stroke();
        }
      }
    }

    // A couple of rail yards add an industrial grain the housing grid lacks.
    const railYard = (wx: number, wy: number, ww: number, wh: number) => {
      const rx = wp(wx),
        ry = wp(wy),
        rw = ww * k,
        rh = wh * k;
      ctx.fillStyle = '#3a352e';
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeStyle = 'rgba(150,150,150,.45)';
      ctx.lineWidth = 1.4 * k;
      for (let ly = ry + 12 * k; ly < ry + rh - 8 * k; ly += 15 * k) {
        ctx.beginPath();
        ctx.moveTo(rx + 8 * k, ly);
        ctx.lineTo(rx + rw - 8 * k, ly);
        ctx.stroke();
      }
      ctx.fillStyle = 'rgba(40,32,24,.5)';
      for (let tx = rx + 6 * k; tx < rx + rw - 6 * k; tx += 9 * k) ctx.fillRect(tx, ry + 6 * k, 2.4 * k, rh - 12 * k);
    };
    railYard(-OUTSKIRT + 130, 250, 520, 150);
    railYard(WORLD + 150, WORLD - 540, 150, 470);

    const drawRoad = (axis: number[], vertical: boolean, color: string, width: number, dashed = false) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width * k;
      ctx.setLineDash(dashed ? [16 * k, 16 * k] : []);
      for (const n of axis) {
        ctx.beginPath();
        if (vertical) {
          ctx.moveTo(wp(n), 0);
          ctx.lineTo(wp(n), S);
        } else {
          ctx.moveTo(0, wp(n));
          ctx.lineTo(S, wp(n));
        }
        ctx.stroke();
      }
      ctx.setLineDash([]);
    };
    drawRoad(OX, true, P.curb, 24);
    drawRoad(OZ, false, P.curb, 24);
    drawRoad(OX, true, P.asphalt, 16);
    drawRoad(OZ, false, P.asphalt, 16);
    drawRoad(OX.filter((_, i) => i % 2 === 0), true, P.lane, 1.8, true);
    drawRoad(OZ.filter((_, i) => i % 2 === 0), false, P.lane, 1.8, true);

    // Very subtle wear keeps the outer texture from reading as a perfect grid.
    ctx.fillStyle = 'rgba(55,47,40,.16)';
    for (let i = 0; i < 120; i++) {
      const x = wrnd() * S;
      const y = wrnd() * S;
      ctx.fillRect(x, y, (12 + wrnd() * 35) * k, (2 + wrnd() * 5) * k);
    }
    return cv;
  }

  // ---- facade textures per kind (shared; UVs scale per building) ----
  // Each kind bakes a colour + emissive + NORMAL + ROUGHNESS map so flat instanced
  // boxes read as real masonry/glass: recessed windows, proud frames, shiny panes.
  type FacadeTex = { color: THREE.Texture; emissive: THREE.Texture; normal: THREE.Texture; rough: THREE.Texture };
  const facadeCache = new Map<number, FacadeTex>();
  function heightToNormal(heights: Uint8ClampedArray, S: number, strength: number): HTMLCanvasElement {
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const ctx = cv.getContext('2d')!;
    const out = ctx.createImageData(S, S);
    const H = (x: number, y: number) => heights[(((y + S) % S) * S + ((x + S) % S)) * 4] / 255;
    for (let y = 0; y < S; y++)
      for (let x = 0; x < S; x++) {
        const dx = (H(x - 1, y) - H(x + 1, y)) * strength;
        const dy = (H(x, y - 1) - H(x, y + 1)) * strength;
        const len = Math.hypot(dx, dy, 1);
        const i = (y * S + x) * 4;
        out.data[i] = ((dx / len) * 0.5 + 0.5) * 255;
        out.data[i + 1] = ((dy / len) * 0.5 + 0.5) * 255;
        out.data[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
        out.data[i + 3] = 255;
      }
    ctx.putImageData(out, 0, 0);
    return cv;
  }
  function facadeTex(kind: number): FacadeTex {
    const cached = facadeCache.get(kind);
    if (cached) return cached;
    const k = KINDS[kind];
    const cells = 4;
    const u = 36;
    const S = cells * u;
    // colour map (wall + dark-glass windows)
    const c1 = document.createElement('canvas');
    c1.width = c1.height = S;
    const x1 = c1.getContext('2d')!;
    const grd = x1.createLinearGradient(0, 0, 0, S);
    grd.addColorStop(0, css(hexShade(k.wall, 1.12)));
    grd.addColorStop(1, css(hexShade(k.wall, 0.9)));
    x1.fillStyle = grd;
    x1.fillRect(0, 0, S, S);
    const glassDay = k.glass ? css(hexShade(k.win, 0.5)) : css(hexShade(k.wall, 0.55));
    for (let r = 0; r < cells; r++)
      for (let c = 0; c < cells; c++) {
        x1.fillStyle = css(hexShade(k.wall, 0.74));
        x1.fillRect(c * u + 5, r * u + 5, u - 10, u - 10);
        x1.fillStyle = glassDay;
        x1.fillRect(c * u + 8, r * u + 8, u - 16, u - 16);
      }
    // emissive map (which windows light up at night)
    const c2 = document.createElement('canvas');
    c2.width = c2.height = S;
    const x2 = c2.getContext('2d')!;
    x2.fillStyle = '#000';
    x2.fillRect(0, 0, S, S);
    const lit = css(k.win);
    const lrnd = mulberry32(0x9e3779 + kind * 2654435);
    for (let r = 0; r < cells; r++)
      for (let c = 0; c < cells; c++) {
        if (lrnd() < (k.glass ? 0.62 : 0.5)) {
          x2.fillStyle = lit;
          x2.fillRect(c * u + 8, r * u + 8, u - 16, u - 16);
        }
      }
    // height map → normal map (proud window frame, recessed glass pane)
    const ch = document.createElement('canvas');
    ch.width = ch.height = S;
    const xh = ch.getContext('2d')!;
    xh.fillStyle = '#808080';
    xh.fillRect(0, 0, S, S);
    // roughness map (matte masonry, glossy glass)
    const crf = document.createElement('canvas');
    crf.width = crf.height = S;
    const xr = crf.getContext('2d')!;
    xr.fillStyle = '#ececec';
    xr.fillRect(0, 0, S, S);
    const paneRough = k.glass ? '#2a2a2a' : '#9a9a9a';
    for (let r = 0; r < cells; r++)
      for (let c = 0; c < cells; c++) {
        xh.fillStyle = '#a6a6a6'; // frame proud
        xh.fillRect(c * u + 4, r * u + 4, u - 8, u - 8);
        xh.fillStyle = '#5e5e5e'; // glass recessed
        xh.fillRect(c * u + 8, r * u + 8, u - 16, u - 16);
        xr.fillStyle = '#b8b8b8'; // frame
        xr.fillRect(c * u + 4, r * u + 4, u - 8, u - 8);
        xr.fillStyle = paneRough; // pane
        xr.fillRect(c * u + 8, r * u + 8, u - 16, u - 16);
      }
    const cn = heightToNormal(xh.getImageData(0, 0, S, S).data, S, 2.4);

    const mk = (cv: HTMLCanvasElement, srgb: boolean) => {
      const t = new THREE.CanvasTexture(cv);
      if (srgb) t.colorSpace = THREE.SRGBColorSpace;
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      t.repeat.set(2, 2);
      t.anisotropy = maxAniso;
      return t;
    };
    const out: FacadeTex = { color: mk(c1, true), emissive: mk(c2, true), normal: mk(cn, false), rough: mk(crf, false) };
    facadeCache.set(kind, out);
    return out;
  }
  function setGeoColor(geo: THREE.BufferGeometry, hex: number) {
    const c = new THREE.Color(hex);
    const n = geo.attributes.position.count;
    const arr = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      arr[i * 3] = c.r;
      arr[i * 3 + 1] = c.g;
      arr[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  }

  // ---- three objects ----
  let wrap: HTMLDivElement;
  let labelHost: HTMLDivElement;
  let renderer: THREE.WebGLRenderer | undefined;
  let scene: THREE.Scene;
  let camera: THREE.OrthographicCamera;
  let controls: MapControls;
  let composer: EffectComposer;
  let bloom: UnrealBloomPass;
  let gtao: GTAOPass;
  let smaaPass: SMAAPass;
  let gradePass: ShaderPass;
  let sun: THREE.DirectionalLight;
  let rim: THREE.DirectionalLight; // cool anti-sun rim (day only) — replaces some of the removed shadow form
  let hemi: THREE.HemisphereLight;
  let skyDome: THREE.Mesh | undefined; // gradient backdrop so the horizon isn't a flat colour
  let groundMat: THREE.MeshStandardMaterial;
  let water: THREE.Mesh | undefined;
  let waterTimeUniform: { value: number } | undefined;
  // Boats are instanced (hull + cabin), matrices rewritten each frame like the cars.
  const boats: { x: number; z: number; dir: number; speed: number; phase: number }[] = [];
  let boatHullInst: THREE.InstancedMesh | undefined;
  let boatCabinInst: THREE.InstancedMesh | undefined;
  const _boatEuler = new THREE.Euler();
  let markerGroup: THREE.Group;
  let agentGroup: THREE.Group;
  const facadeMats: THREE.MeshStandardMaterial[] = [];
  // Visual-only outskirts keep their own darker materials so they can recede independently.
  const outerFacadeMats: THREE.MeshStandardMaterial[] = [];
  // Storefront + billboard materials: lit a little by day, neon-bright at night.
  const signMats: THREE.MeshStandardMaterial[] = [];
  // Authored CC0 hero models (Blender → glTF) replace the procedural plot buildings when loaded.
  // Each is scaled so its larger ground dimension matches the target footprint below.
  const HERO_FOOTPRINT: Record<BuildingArt, number> = {
    corner: 78, stashhouse: 66, bodega: 74, garage: 108, tenement: 70,
    loft: 96, motel: 110, warehouse: 124, factory: 120, tower: 96,
  };
  const heroModels: Partial<Record<BuildingArt, { scene: THREE.Group; size: THREE.Vector3 }>> = {};
  // Hero-model materials whose lit-window emissive should glow at night (driven in applyTimeOfDay).
  const heroEmitMats = new Set<THREE.MeshStandardMaterial>();
  // Subtle green ground glow under owned plots so the empire reads at a glance.
  let ownedTintGeo: THREE.CircleGeometry;
  let ownedTintMat: THREE.MeshBasicMaterial;
  let maxAniso = 4;
  let raf = 0;
  let destroyed = false;

  // ---- graphics quality (see gfxSettings.ts) ----
  let gfxUnsub: (() => void) | undefined;
  let gfxAdaptive = false; // 'auto' preset → tune render scale to hold the FPS target
  let gfxBaseScale = 1.1; // the preset's requested scale; auto multiplies this by gfxDynScale
  let gfxDynScale = 1; // adaptive multiplier (auto only), clamped to [AUTO_MIN_SCALE, 1]
  let gfxAccum = 0;
  let gfxFrames = 0;

  let current: City;
  let unsub: (() => void) | undefined;
  let ro: ResizeObserver | undefined;

  // ---- agents ----
  type Dealer = { g: THREE.Group; bid: number; x: number; y: number; tx: number; ty: number; home: { x: number; y: number }; state: 'rest' | 'out' | 'deal' | 'back'; timer: number; speed: number; cust: THREE.Object3D | null };
  // Cars + peds are GPU-instanced (index = position in the array); no per-agent Object3D.
  type Car = { seg: Seg; t: number; dir: number; speed: number };
  type Ped = { x: number; y: number; tx: number; ty: number; speed: number };
  type Riser = { el: HTMLDivElement; x: number; y: number; z: number; life: number };
  // sub/important/lastSub/shown are caches so the per-frame label pass and the per-tick sublabel
  // refresh never hit querySelector/classList or write styles that haven't changed.
  type Marker = { el: HTMLDivElement; sub: HTMLDivElement; x: number; y: number; z: number; clickable: THREE.Object3D; ring: THREE.Mesh; ringScale: number; ownedRing: boolean; important: boolean; lastSub: string; shown: boolean; pin?: THREE.Object3D };
  type Steam = { mesh: THREE.Mesh; baseY: number; phase: number };
  type Signal = { red: THREE.MeshStandardMaterial; green: THREE.MeshStandardMaterial; phase: number };
  const districtEls: { el: HTMLDivElement; x: number; z: number }[] = [];
  let dealers: Dealer[] = [];
  let cars: Car[] = [];
  let peds: Ped[] = [];
  let risers: Riser[] = [];
  let markers: Marker[] = [];
  // markers pre-sorted by label priority (active>owned>affordable>plot); the priority classes are
  // fixed at build time, so this only needs recomputing when markers are rebuilt (syncBuildings).
  let orderedMarkers: Marker[] = [];
  let hoveredEl: HTMLDivElement | null = null;
  // Raycast targets for hover picking, rebuilt with the markers (avoids a per-pointermove map()).
  let clickables: THREE.Object3D[] = [];
  // Marker/district labels are DOM writes — only reproject them when the camera actually moved,
  // the hover changed, or the markers were rebuilt. Risers animate every frame regardless.
  let labelsDirty = true;
  let lastCamSig = NaN;
  const streetLampMats: THREE.MeshStandardMaterial[] = [];
  const steams: Steam[] = [];
  const signals: Signal[] = [];
  const sold: Record<number, number> = {};
  const made: Record<number, number> = {};
  const prevProd: Record<number, number> = {};
  let followDealer: Dealer | null = null;

  function moveToward(o: { x: number; y: number; tx: number; ty: number; speed: number }, dt: number) {
    const dx = o.tx - o.x,
      dy = o.ty - o.y;
    const d = Math.hypot(dx, dy);
    if (d < 1.5) return true;
    const s = o.speed * dt;
    if (s >= d) {
      o.x = o.tx;
      o.y = o.ty;
      return true;
    }
    o.x += (dx / d) * s;
    o.y += (dy / d) * s;
    return false;
  }

  // shared geometries/materials for agents
  let carGeo: THREE.BoxGeometry, carHeadGeo: THREE.BoxGeometry, pedGeo: THREE.CylinderGeometry;
  let headMat: THREE.MeshStandardMaterial, pedMatA: THREE.MeshStandardMaterial;
  // Cars + peds are drawn as InstancedMesh (80 peds -> 1 draw, 40 cars -> 2 draws) and
  // their matrices are rewritten each frame in step(). Scratch objects are hoisted to
  // avoid per-frame allocation.
  let pedInst: THREE.InstancedMesh | undefined;
  let carBodyInst: THREE.InstancedMesh | undefined;
  let carHeadInst: THREE.InstancedMesh | undefined;
  const _agM = new THREE.Matrix4();
  const _agT = new THREE.Matrix4();
  const _agQ = new THREE.Quaternion();
  const _agP = new THREE.Vector3();
  const _agS = new THREE.Vector3(1, 1, 1);
  const _agY = new THREE.Vector3(0, 1, 0);
  const _agCol = new THREE.Color();
  const _carBodyOff = new THREE.Matrix4().makeTranslation(0, 2.6, 0);
  const _carHeadOff = new THREE.Matrix4().makeTranslation(7.4, 2.3, 0);
  const _boatCabinOff = new THREE.Matrix4().makeTranslation(-4, 7, 0);

  function spawnCar(seg: Seg) {
    const i = cars.length;
    cars.push({ seg, t: Math.random(), dir: Math.random() < 0.5 ? 1 : -1, speed: rand(0.05, 0.1) });
    // per-car paint via instanceColor (white base material is multiplied by this)
    if (carBodyInst) {
      carBodyInst.setColorAt(i, _agCol.set(CARCOLORS[(Math.random() * CARCOLORS.length) | 0]));
      if (carBodyInst.instanceColor) carBodyInst.instanceColor.needsUpdate = true;
    }
  }
  function spawnPed() {
    const p = roadPoint();
    const t = roadPoint();
    peds.push({ x: p.x, y: p.y, tx: t.x, ty: t.y, speed: rand(12, 22) });
  }
  function makeFigure(color: number, scale: number) {
    const g = new THREE.Group();
    const body = new THREE.Mesh(new THREE.CylinderGeometry(2 * scale, 2.4 * scale, 5 * scale, 6), new THREE.MeshStandardMaterial({ color, roughness: 0.8 }));
    body.position.y = 3 * scale;
    const head = new THREE.Mesh(new THREE.SphereGeometry(1.7 * scale, 8, 6), new THREE.MeshStandardMaterial({ color: 0xe6c39a, roughness: 0.7 }));
    head.position.y = 6.5 * scale;
    g.add(body, head);
    return g;
  }
  function spawnDealer(bid: number, hx: number, hy: number) {
    const g = makeFigure(0x36c45e, 1.8);
    g.position.set(hx, 0, hy);
    agentGroup.add(g);
    dealers.push({ g, bid, x: hx, y: hy, tx: hx, ty: hy, home: { x: hx, y: hy }, state: 'rest', timer: rand(0.3, 1.5), speed: rand(48, 66), cust: null });
  }
  function spawnRiser(x: number, z: number, label: string, cls: string) {
    const el = document.createElement('div');
    el.className = 'riser ' + cls;
    el.textContent = label;
    labelHost.appendChild(el);
    risers.push({ el, x, y: 40, z, life: 1 });
  }

  // ---- bespoke property buildings + restrained selection markers ----
  function propertyBuilding(art: BuildingArt, owned: boolean) {
    // Authored model when available; otherwise the procedural box building below.
    const hero = heroModels[art];
    if (hero) {
      const hg = new THREE.Group();
      const model = hero.scene.clone(true);
      const fp = HERO_FOOTPRINT[art];
      const s = fp / Math.max(hero.size.x, hero.size.z, 0.001);
      model.scale.setScalar(s);
      hg.add(model);
      return { group: hg, height: hero.size.y * s, radius: fp / 2 + 6 };
    }
    const g = new THREE.Group();
    const wallColor: Record<BuildingArt, number> = {
      corner: 0xa37f58,
      stashhouse: 0x675b51,
      bodega: 0x9b6044,
      garage: 0x687078,
      tenement: 0x874e3c,
      loft: 0x795244,
      motel: 0x8b7862,
      warehouse: 0x66584f,
      factory: 0x594b45,
      tower: 0x41667b,
    };
    const wall = new THREE.MeshStandardMaterial({ color: wallColor[art], roughness: art === 'tower' ? 0.3 : 0.88, metalness: art === 'tower' ? 0.45 : 0.02 });
    const roof = new THREE.MeshStandardMaterial({ color: owned ? 0x3d4d3f : 0x343536, roughness: 0.9 });
    const glass = new THREE.MeshStandardMaterial({
      color: 0x223a46,
      emissive: owned ? 0x56e58a : 0xffb46a,
      emissiveIntensity: owned ? 1.5 : 0.18,
      roughness: 0.18,
      metalness: 0.55,
      envMapIntensity: 1.5,
    });
    const accent = new THREE.MeshStandardMaterial({
      color: owned ? 0x2a8f50 : 0x86672b,
      emissive: owned ? 0x36e276 : 0xf0ad42,
      emissiveIntensity: owned ? 1.7 : 0.3,
      roughness: 0.45,
    });
    const dark = new THREE.MeshStandardMaterial({ color: 0x26272a, roughness: 0.9 });
    const addBox = (w: number, h: number, d: number, x = 0, y = h / 2, z = 0, mat: THREE.Material = wall) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      m.receiveShadow = true;
      m.castShadow = true;
      g.add(m);
      return m;
    };
    const windows = (w: number, h: number, z: number, y: number, count: number) => {
      for (let i = 0; i < count; i++) addBox(w, h, 1.2, (i - (count - 1) / 2) * (w + 6), y, z, glass);
    };
    let height = 44;
    switch (art) {
      case 'corner':
        addBox(72, 34, 58);
        addBox(76, 5, 15, 0, 23, -35, accent);
        windows(13, 14, -30, 15, 3);
        height = 38;
        break;
      case 'stashhouse': {
        addBox(58, 36, 54);
        const r = new THREE.Mesh(new THREE.ConeGeometry(45, 24, 4), roof);
        r.position.y = 48;
        r.rotation.y = Math.PI / 4;
        g.add(r);
        addBox(12, 22, 2, 0, 11, -28, dark);
        windows(11, 11, -28, 23, 2);
        height = 62;
        break;
      }
      case 'bodega':
        addBox(70, 42, 62);
        addBox(76, 6, 18, 0, 26, -39, accent);
        addBox(42, 11, 3, 0, 42, -32, accent);
        windows(14, 18, -32, 17, 3);
        height = 50;
        break;
      case 'garage':
        addBox(104, 31, 68);
        for (let i = -1; i <= 1; i++) addBox(27, 23, 2, i * 31, 12, -35, dark);
        addBox(108, 4, 9, 0, 31, -37, accent);
        height = 36;
        break;
      case 'tenement':
        addBox(62, 96, 66);
        windows(12, 12, -34, 22, 3);
        windows(12, 12, -34, 47, 3);
        windows(12, 12, -34, 72, 3);
        for (let y = 29; y < 85; y += 24) {
          addBox(48, 2, 8, 0, y, -39, dark);
          addBox(2, 24, 2, -23, y - 10, -42, dark);
        }
        height = 100;
        break;
      case 'loft':
        addBox(92, 68, 74);
        addBox(54, 24, 48, 15, 80, 8);
        windows(16, 18, -38, 21, 4);
        windows(16, 18, -38, 49, 4);
        height = 94;
        break;
      case 'motel':
        addBox(104, 44, 32, 0, 22, 20);
        addBox(34, 44, 92, -35, 22, -10);
        for (let i = -1; i <= 1; i++) addBox(19, 18, 2, i * 27 + 10, 22, 3, glass);
        addBox(6, 58, 6, 58, 29, 10, accent);
        addBox(34, 18, 4, 58, 53, 10, accent);
        height = 65;
        break;
      case 'warehouse':
        addBox(122, 37, 94);
        addBox(76, 8, 98, 0, 41, 0, roof);
        addBox(42, 27, 2, 0, 14, -48, dark);
        windows(15, 12, -48, 28, 4);
        height = 49;
        break;
      case 'factory': {
        addBox(112, 55, 88);
        addBox(64, 27, 60, 18, 68, 5);
        for (const x of [-37, 37]) {
          const stack = new THREE.Mesh(new THREE.CylinderGeometry(7, 9, 88, 10), dark);
          stack.position.set(x, 52, 18);
          g.add(stack);
        }
        windows(13, 13, -45, 28, 5);
        height = 96;
        break;
      }
      case 'tower':
        addBox(92, 118, 84);
        addBox(68, 62, 62, 0, 149, 0);
        addBox(38, 44, 38, 0, 202, 0);
        windows(13, 16, -43, 25, 5);
        windows(13, 16, -43, 54, 5);
        windows(13, 16, -43, 84, 5);
        addBox(8, 28, 8, 0, 238, 0, accent);
        height = 252;
        break;
    }
    // Every property gets a paved footprint, readable entrance and ownership branding.
    const pad = new THREE.Mesh(new THREE.BoxGeometry(art === 'tower' ? 128 : 142, 2.4, art === 'tower' ? 122 : 132), new THREE.MeshStandardMaterial({ color: 0x7e7769, roughness: 0.98 }));
    pad.position.y = -0.2;
    pad.receiveShadow = true;
    g.add(pad);
    addBox(16, 25, 2.2, 0, 12.5, art === 'motel' ? 2 : art === 'corner' ? -30 : -((art === 'warehouse' || art === 'factory') ? 48 : 34), dark);
    if (owned) {
      addBox(30, 5, 4, 0, Math.min(height - 6, 34), art === 'motel' ? 0 : -((art === 'warehouse' || art === 'factory') ? 49 : 35), accent);
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(24, 12), new THREE.MeshBasicMaterial({ color: 0x39e070, side: THREE.DoubleSide }));
      flag.position.set(22, height + 14, 0);
      g.add(flag);
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 32, 6), dark);
      pole.position.set(10, height + 4, 0);
      g.add(pole);
    }
    const radius = art === 'tower' ? 68 : art === 'factory' || art === 'warehouse' || art === 'garage' ? 82 : 72;
    // Procedural fallback owns its geometries/materials (hero clones share theirs) — tag them so
    // syncBuildings can dispose on rebuild instead of leaking a set of boxes per marker.
    g.traverse((o) => (o.userData.procedural = true));
    return { group: g, height, radius };
  }

  function addStreetDetails() {
    // Static street furniture is GPU-instanced by (geometry, material) so hundreds of tiny
    // meshes collapse to ~a dozen draw calls. Only per-frame-animated pieces (traffic-signal
    // lamps, steam puffs) stay as individual meshes. propRnd() call order is preserved so the
    // deterministic layout is byte-identical to before.
    const cityProps = new THREE.Group();
    const metal = new THREE.MeshStandardMaterial({ color: 0x282b2d, roughness: 0.72, metalness: 0.55 });
    const benchMat = new THREE.MeshStandardMaterial({ color: 0x765238, roughness: 0.9 });
    const binMat = new THREE.MeshStandardMaterial({ color: 0x355248, roughness: 0.85 });
    const hydrantMat = new THREE.MeshStandardMaterial({ color: 0xa83d35, roughness: 0.65 });
    const shelterMat = new THREE.MeshStandardMaterial({ color: 0x72909d, transparent: true, opacity: 0.58, roughness: 0.22, metalness: 0.35 });
    const lampMat = new THREE.MeshStandardMaterial({ color: 0xffd391, emissive: 0xffa33b, emissiveIntensity: 0 });
    streetLampMats.push(lampMat);
    // shared geometries (each reused across all instances of its kind)
    const poleGeo = new THREE.CylinderGeometry(1.15, 1.5, 24, 7);
    const bulbGeo = new THREE.SphereGeometry(2.8, 8, 6);
    const sigPoleGeo = new THREE.CylinderGeometry(1.2, 1.5, 28, 7);
    const sigBoxGeo = new THREE.BoxGeometry(6, 12, 5);
    const sigLampGeo = new THREE.SphereGeometry(1.65, 8, 6);
    const shelterGeo = new THREE.BoxGeometry(30, 18, 3);
    const seatGeo = new THREE.BoxGeometry(22, 3, 7);
    const binGeo = new THREE.BoxGeometry(9, 8, 7);
    const ventGeo = new THREE.CylinderGeometry(2.5, 3, 3, 8);
    const benchGeo = new THREE.BoxGeometry(12, 5, 4);
    const hydrantGeo = new THREE.CylinderGeometry(2.3, 2.6, 6, 7);
    const propRnd = mulberry32(0xc17a11fe);
    const occupied = (x: number, z: number) =>
      CITY_PLOTS.some((p) => {
        const pp = propertyPosition(p);
        return Math.hypot(x - pp.x, z - pp.z) < PROPERTY_CLEARANCE + 20;
      });
    const tr = (x: number, y: number, z: number) => new THREE.Matrix4().makeTranslation(x, y, z);
    // transform buckets per (geometry, material) group
    const poleMx: THREE.Matrix4[] = [], bulbMx: THREE.Matrix4[] = [];
    const sigPoleMx: THREE.Matrix4[] = [], sigBoxMx: THREE.Matrix4[] = [];
    const shelterMx: THREE.Matrix4[] = [], seatMx: THREE.Matrix4[] = [], binMx: THREE.Matrix4[] = [], ventMx: THREE.Matrix4[] = [];
    // props: [benchGeo+benchMat, benchGeo+hydrantMat, hydrantGeo+benchMat, hydrantGeo+hydrantMat]
    const propMx: THREE.Matrix4[][] = [[], [], [], []];

    // Streetlights and small furniture make the streets legible at human scale.
    for (const s of segs) {
      if (!s.major) continue;
      const len = Math.hypot(s.x2 - s.x1, s.y2 - s.y1);
      const ux = (s.x2 - s.x1) / len;
      const uz = (s.y2 - s.y1) / len;
      const px = -uz;
      const pz = ux;
      for (let d = 95; d < len - 60; d += 175) {
        for (const side of [-1, 1]) {
          const x = s.x1 + ux * d + px * 22 * side;
          const z = s.y1 + uz * d + pz * 22 * side;
          if (occupied(x, z)) continue;
          if (z > WATER_Z0 - 6) continue; // no lamps stranded out in the harbour
          poleMx.push(tr(x, 12, z));
          bulbMx.push(tr(x, 25, z));
          // No real PointLights: an A/B showed 18 forward-rendered lights were the single biggest
          // night GPU cost yet visually indistinguishable — the emissive bulbs + bloom + the baked
          // ground-glow map carry the entire streetlight read. So night now costs as little as day.
          if (propRnd() < 0.34) {
            const box = propRnd() < 0.55; // geometry pick (matches original order)
            const bench = propRnd() < 0.55; // material pick
            propMx[(box ? 0 : 2) + (bench ? 0 : 1)].push(tr(x + ux * 8, box ? 2.5 : 3, z + uz * 8));
          }
        }
      }
    }

    // Traffic signals at major crossings (poles/boxes instanced; the red/green lamps stay
    // individual because signals[] animates their emissive per crossing).
    VX.forEach((x, ix) =>
      VY.forEach((z, iz) => {
        if (ix % 2 || iz % 2) return;
        sigPoleMx.push(tr(x + 19, 14, z + 19));
        sigBoxMx.push(tr(x + 19, 25, z + 19));
        const red = new THREE.MeshStandardMaterial({ color: 0x401515, emissive: 0xff2f2f, emissiveIntensity: 0 });
        const green = new THREE.MeshStandardMaterial({ color: 0x123d20, emissive: 0x3df078, emissiveIntensity: 1.5 });
        const r = new THREE.Mesh(sigLampGeo, red);
        r.position.set(x + 19, 28, z + 16.3);
        const gr = new THREE.Mesh(sigLampGeo, green);
        gr.position.set(x + 19, 22, z + 16.3);
        cityProps.add(r, gr);
        signals.push({ red, green, phase: (ix + iz) * 1.7 });
      })
    );

    // Bus shelters, dumpsters, benches and steam vents around blocks.
    blocks.forEach((bl, i) => {
      if (bl.type === 'water') return;
      const x = bl.x + 35;
      const z = bl.y + bl.h - 27;
      if (occupied(x, z)) return;
      if (i % 6 === 1) {
        shelterMx.push(tr(x + 15, 9, z));
        seatMx.push(tr(x + 15, 4, z + 4));
      } else if (i % 4 === 0) {
        binMx.push(new THREE.Matrix4().makeRotationY(propRnd() * 0.5).setPosition(x, 4, z));
      }
      if (i % 7 === 2) {
        ventMx.push(tr(x + 18, 1.5, z - 9));
        for (let p = 0; p < 3; p++) {
          const puff = new THREE.Mesh(
            new THREE.SphereGeometry(3 + p, 7, 5),
            new THREE.MeshBasicMaterial({ color: 0xc5c9c8, transparent: true, opacity: 0.2, depthWrite: false })
          );
          puff.position.set(x + 18, 5 + p * 6, z - 9);
          cityProps.add(puff);
          steams.push({ mesh: puff, baseY: 5 + p * 6, phase: p * 1.8 + i });
        }
      }
    });

    const buildInst = (geo: THREE.BufferGeometry, mat: THREE.Material, arr: THREE.Matrix4[]) => {
      if (!arr.length) return;
      const im = new THREE.InstancedMesh(geo, mat, arr.length);
      im.instanceMatrix.setUsage(THREE.StaticDrawUsage);
      arr.forEach((m, i) => im.setMatrixAt(i, m));
      im.instanceMatrix.needsUpdate = true;
      cityProps.add(im);
    };
    buildInst(poleGeo, metal, poleMx);
    buildInst(bulbGeo, lampMat, bulbMx);
    buildInst(sigPoleGeo, metal, sigPoleMx);
    buildInst(sigBoxGeo, metal, sigBoxMx);
    buildInst(shelterGeo, shelterMat, shelterMx);
    buildInst(seatGeo, benchMat, seatMx);
    buildInst(binGeo, binMat, binMx);
    buildInst(ventGeo, metal, ventMx);
    buildInst(benchGeo, benchMat, propMx[0]);
    buildInst(benchGeo, hydrantMat, propMx[1]);
    buildInst(hydrantGeo, benchMat, propMx[2]);
    buildInst(hydrantGeo, hydrantMat, propMx[3]);
    scene.add(cityProps);
  }

  let ringGeo: THREE.RingGeometry, hitGeo: THREE.CylinderGeometry, hitMat: THREE.MeshBasicMaterial;
  let ringMatOwned: THREE.MeshBasicMaterial, ringMatForSale: THREE.MeshBasicMaterial;
  let ringMatOwnedHover: THREE.MeshBasicMaterial, ringMatForSaleHover: THREE.MeshBasicMaterial;
  // Buy-confirmation card: anchored above the selected for-sale plot, tracks the camera.
  let buySel: CityPlot | null = null;
  let buyAnchor = { x: 0, y: 0, z: 0 };
  let buyEl: HTMLDivElement | undefined;
  $: buyAffordable = buySel !== null && current !== undefined && current.cash >= buySel.cost;
  function openBuy(plot: CityPlot) {
    const m = markers.find((mk) => mk.clickable.userData.plotId === plot.id);
    if (m) buyAnchor = { x: m.x, y: m.y, z: m.z };
    buySel = plot;
  }
  function confirmBuy() {
    if (buySel) dispatch({ type: 'buyPlot', plotId: buySel.id });
    buySel = null;
  }
  // Owned-building sublabel; stock only shows once there is some (📦 0 is dead information).
  function subText(b: Building): string {
    const prod = Math.floor(b.res.product);
    return `👥 ${b.colonists.length}/${b.maxCrew}` + (prod > 0 ? ` · 📦 ${prod}` : '');
  }
  function addMarker(b: Building | CityPlot, owned: boolean) {
    const grp = new THREE.Group();
    const pp = propertyPosition(b);
    grp.position.set(pp.x, 0, pp.z);
    const property = propertyBuilding(b.art, owned);
    grp.add(property.group);
    if (owned) {
      const tint = new THREE.Mesh(ownedTintGeo, ownedTintMat);
      tint.rotation.x = -Math.PI / 2;
      tint.position.y = 1.3;
      const ts = property.radius * 2.1;
      tint.scale.set(ts, ts, 1);
      grp.add(tint);
    }
    const ring = new THREE.Mesh(ringGeo, owned ? ringMatOwned : ringMatForSale);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 2.2;
    ring.scale.setScalar(property.radius);
    const hit = new THREE.Mesh(hitGeo, hitMat);
    hit.position.y = Math.max(60, property.height / 2);
    hit.scale.set(property.radius / 34, Math.max(1, property.height / 100), property.radius / 34);
    hit.userData = owned ? { buildingId: (b as Building).id } : { plotId: (b as CityPlot).id };
    grp.add(ring, hit);
    markerGroup.add(grp);

    const el = document.createElement('div');
    const affordable = !owned && current.cash >= (b as CityPlot).cost;
    const active = owned && current.activeId === (b as Building).id;
    // markers are born hidden (mhidden) and fade in on their first projection pass
    el.className = 'marker mhidden ' + (owned ? 'owned' : 'plot') + (affordable ? ' affordable' : '') + (active ? ' active' : '');
    const name = document.createElement('div');
    name.className = 'mname';
    name.textContent = b.name;
    const sub = document.createElement('div');
    sub.className = 'msub';
    sub.textContent = owned ? subText(b as Building) : money((b as CityPlot).cost);
    el.append(name, sub);
    // labels are click targets too: enter an owned building / open the buy card for a plot
    el.addEventListener('click', () => {
      if (owned) {
        buySel = null;
        dispatch({ type: 'switchBuilding', buildingId: (b as Building).id });
        emit('enter', (b as Building).id);
      } else {
        openBuy(b as CityPlot);
      }
    });
    labelHost.appendChild(el);
    // priority classes are fixed once the marker DOM is built (see orderedMarkers), so
    // "important" can be cached instead of re-reading classList every frame.
    markers.push({ el, sub, x: pp.x, y: property.height + 48, z: pp.z, clickable: hit, ring, ringScale: property.radius, ownedRing: owned, important: owned || affordable || active, lastSub: sub.textContent ?? '', shown: false });
  }

  function syncBuildings() {
    if (!scene) return;
    // Dispose geometry/materials owned by procedural fallback buildings before dropping them;
    // everything else in markerGroup (hero clones, rings, tints, hit volumes) is shared.
    {
      const geos = new Set<THREE.BufferGeometry>();
      const mats = new Set<THREE.Material>();
      markerGroup.traverse((o) => {
        const m = o as THREE.Mesh;
        if (m.isMesh && m.userData.procedural) {
          geos.add(m.geometry as THREE.BufferGeometry);
          for (const mm of Array.isArray(m.material) ? m.material : [m.material]) mats.add(mm as THREE.Material);
        }
      });
      geos.forEach((g) => g.dispose());
      mats.forEach((m) => m.dispose());
    }
    markerGroup.clear();
    markers.forEach((m) => m.el.remove());
    markers = [];
    hoveredEl = null;
    dealers.forEach((d) => {
      d.cust && agentGroup.remove(d.cust);
      agentGroup.remove(d.g);
    });
    dealers = [];
    risers.forEach((r) => r.el.remove());
    risers = [];
    followDealer = null;
    for (const p of current.plots) addMarker(p, false);
    for (const b of current.buildings) {
      addMarker(b, true);
      const pp = propertyPosition(b);
      spawnDealer(b.id, pp.x, pp.z + 48);
      prevProd[b.id] = b.res.product;
      sold[b.id] = sold[b.id] ?? 0;
      made[b.id] = 0;
    }
    // Pre-sort for label de-crowding (priority classes are fixed once the marker DOM is built).
    const priority = (m: Marker) => (m.el.classList.contains('active') ? 3 : m.el.classList.contains('owned') ? 2 : m.el.classList.contains('affordable') ? 1 : 0);
    orderedMarkers = [...markers].sort((a, b) => priority(b) - priority(a));
    clickables = markers.map((m) => m.clickable);
    labelsDirty = true;
  }

  // ---- camera / picking ----
  let viewSize = 1700;
  function frustum() {
    const r = renderer!.domElement;
    const a = r.clientWidth / r.clientHeight || 1;
    camera.left = (-viewSize * a) / 2;
    camera.right = (viewSize * a) / 2;
    camera.top = viewSize / 2;
    camera.bottom = -viewSize / 2;
    camera.updateProjectionMatrix();
  }

  // Apply the pixel ratio (preset scale × adaptive factor, capped by the device) and re-size every
  // buffer. Cheap; called on quality change and ~1×/s while auto-adapting.
  function setRenderScale(scale: number) {
    if (!renderer || !composer) return;
    renderer.setPixelRatio(Math.min(scale, window.devicePixelRatio || 1));
    const w = wrap.clientWidth,
      h = wrap.clientHeight;
    renderer.setSize(w, h);
    composer.setSize(w, h);
    gtao.setSize(Math.max(1, w * 0.5), Math.max(1, h * 0.5));
    bloom.setSize(Math.max(1, w * 0.5), Math.max(1, h * 0.5));
  }

  // Apply a full quality preset: resolution, composer MSAA, GTAO/SMAA passes, and the active
  // street-light count. Changing MSAA re-allocates the composer targets (one-time), and changing
  // the light count recompiles lit materials once — both fine for a user-initiated setting change.
  function applyGfx(q: GfxQuality) {
    if (!renderer || !composer) return;
    const p = PRESETS[q];
    gfxAdaptive = p.adaptive;
    gfxBaseScale = p.renderScale;
    gfxDynScale = 1;
    gfxAccum = 0;
    gfxFrames = 0;
    const maxS = renderer.capabilities.maxSamples ?? 4;
    const samples = Math.min(p.msaa, maxS);
    if (composer.renderTarget1.samples !== samples) {
      composer.renderTarget1.samples = samples;
      composer.renderTarget2.samples = samples;
      composer.renderTarget1.dispose();
      composer.renderTarget2.dispose();
    }
    gtao.enabled = p.gtao;
    if (smaaPass) smaaPass.enabled = p.smaa;
    setRenderScale(gfxBaseScale);
  }
  const mapPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  const mapViewRay = new THREE.Raycaster();
  // Guard the central 58% of the viewport. Requiring all four corners of a
  // rectangular screen to fit inside a rotated square city over-zooms severely.
  const MAP_GUARD = 0.58;
  const mapCorners = [
    new THREE.Vector2(-MAP_GUARD, -MAP_GUARD),
    new THREE.Vector2(MAP_GUARD, -MAP_GUARD),
    new THREE.Vector2(MAP_GUARD, MAP_GUARD),
    new THREE.Vector2(-MAP_GUARD, MAP_GUARD),
  ];
  const mapHits = mapCorners.map(() => new THREE.Vector3());
  const MAP_EDGE_INSET = 8;
  const CAMERA_BLEED = OUTSKIRT_DETAIL * 0.22;
  function mapViewBounds() {
    for (let i = 0; i < mapCorners.length; i++) {
      mapViewRay.setFromCamera(mapCorners[i], camera);
      if (!mapViewRay.ray.intersectPlane(mapPlane, mapHits[i])) return null;
    }
    return {
      minX: Math.min(...mapHits.map((p) => p.x)),
      maxX: Math.max(...mapHits.map((p) => p.x)),
      minZ: Math.min(...mapHits.map((p) => p.z)),
      maxZ: Math.max(...mapHits.map((p) => p.z)),
    };
  }
  function clampMapView() {
    let bounds = mapViewBounds();
    if (!bounds) return;
    const cameraMin = -CAMERA_BLEED + MAP_EDGE_INSET;
    const cameraMax = WORLD + CAMERA_BLEED - MAP_EDGE_INSET;
    const usable = cameraMax - cameraMin;
    const requiredZoom = camera.zoom * Math.max((bounds.maxX - bounds.minX) / usable, (bounds.maxZ - bounds.minZ) / usable) * 1.015;
    controls.minZoom = Math.max(0.05, requiredZoom);
    if (camera.zoom < controls.minZoom) {
      camera.zoom = controls.minZoom;
      camera.updateProjectionMatrix();
      bounds = mapViewBounds();
      if (!bounds) return;
    }
    const left = bounds.minX - controls.target.x;
    const right = bounds.maxX - controls.target.x;
    const near = bounds.minZ - controls.target.z;
    const far = bounds.maxZ - controls.target.z;
    const minTargetX = cameraMin - left;
    const maxTargetX = cameraMax - right;
    const minTargetZ = cameraMin - near;
    const maxTargetZ = cameraMax - far;
    const nextX = minTargetX > maxTargetX ? WORLD / 2 : THREE.MathUtils.clamp(controls.target.x, minTargetX, maxTargetX);
    const nextZ = minTargetZ > maxTargetZ ? WORLD / 2 : THREE.MathUtils.clamp(controls.target.z, minTargetZ, maxTargetZ);
    const dx = nextX - controls.target.x;
    const dz = nextZ - controls.target.z;
    if (Math.abs(dx) > 0.001 || Math.abs(dz) > 0.001) {
      controls.target.x = nextX;
      controls.target.z = nextZ;
      camera.position.x += dx;
      camera.position.z += dz;
      camera.updateMatrixWorld();
    }
  }
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let downX = 0,
    downY = 0,
    dragMoved = false;
  function onDown(e: PointerEvent) {
    downX = e.clientX;
    downY = e.clientY;
    dragMoved = false;
  }
  function setHover(nextHover: HTMLDivElement | null) {
    if (nextHover === hoveredEl) return;
    // swap the selection ring to its brighter hover material (+ a slight grow)
    const prevM = markers.find((m) => m.el === hoveredEl);
    if (prevM) {
      prevM.ring.material = prevM.ownedRing ? ringMatOwned : ringMatForSale;
      prevM.ring.scale.setScalar(prevM.ringScale);
    }
    const nextM = nextHover ? markers.find((m) => m.el === nextHover) : undefined;
    if (nextM) {
      nextM.ring.material = nextM.ownedRing ? ringMatOwnedHover : ringMatForSaleHover;
      nextM.ring.scale.setScalar(nextM.ringScale * 1.07);
    }
    hoveredEl = nextHover;
    labelsDirty = true;
  }
  function onMove(e: PointerEvent) {
    if (Math.abs(e.clientX - downX) + Math.abs(e.clientY - downY) > 4) dragMoved = true;
    if (!renderer || !markers.length) return;
    // While left-dragging the map, skip the hover raycast and let .cwrap:active's
    // grabbing cursor through (an inline 'pointer' would override it).
    if (e.buttons & 1 && dragMoved) {
      setHover(null);
      renderer.domElement.style.cursor = '';
      return;
    }
    // Hover a building to reveal its label even at wide zoom (and show a pointer cursor).
    const r = renderer.domElement.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    const hits = ray.intersectObjects(clickables, false);
    setHover(hits.length ? markers.find((m) => m.clickable === hits[0].object)?.el ?? null : null);
    renderer.domElement.style.cursor = hits.length ? 'pointer' : '';
  }
  const onCtx = (e: Event) => e.preventDefault();
  function onUp(e: PointerEvent) {
    if (dragMoved || e.button !== 0) return;
    const r = renderer!.domElement.getBoundingClientRect();
    ndc.set(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    const hits = ray.intersectObjects(markerGroup.children, true);
    const hit = hits.find((h) => h.object.userData.plotId || h.object.userData.buildingId);
    if (!hit) {
      buySel = null; // clicking empty ground dismisses the buy card
      return;
    }
    const ud = hit.object.userData;
    if (ud.buildingId) {
      buySel = null;
      dispatch({ type: 'switchBuilding', buildingId: ud.buildingId });
      emit('enter', ud.buildingId);
    } else if (ud.plotId) {
      // ask before spending — the card shows price/size and a Buy button
      const plot = current.plots.find((p) => p.id === ud.plotId);
      if (plot) openBuy(plot);
    }
  }

  // ---- day / night ----
  const COL = {
    skyDay: new THREE.Color(0xd7cdb8),
    skyNight: new THREE.Color(0x26344a),
    sunDay: new THREE.Color(0xfff1d6),
    sunDusk: new THREE.Color(0xff9d5a),
    sunNight: new THREE.Color(0x5a6f9a),
    hemiSkyDay: new THREE.Color(0xbcd0e8), // cooler sky fill → shadowed faces go cool against warm sun
    hemiSkyNight: new THREE.Color(0x394b65),
    hemiGndDay: new THREE.Color(0x9c8b6a),
    hemiGndNight: new THREE.Color(0x171b22),
    domeDay: new THREE.Color(0xcdd2dc),
    domeNight: new THREE.Color(0x1a2436),
  };
  const tmpA = new THREE.Color();
  const tmpB = new THREE.Color();
  // Last time-of-day the (expensive) colour/emissive block ran. NaN forces the first call.
  let lastToD = NaN;
  function applyTimeOfDay(t: number) {
    const f = timeFrac(t);
    const nf = nightFraction(f);
    // Sun position tracks controls.target and must update EVERY frame: it also anchors the
    // shadow-camera frustum, which drifts off-screen during a pan if this is throttled.
    const ang = (f - 0.25) * Math.PI * 2; // noon overhead
    // A lower, more raking sun (noon elev ~20° instead of ~34°) makes lit vs. self-shadowed faces
    // do the modelling that cast shadows used to — the scene reads far less flat without them.
    const elev = Math.max(0.06, Math.pow(Math.sin(f * Math.PI), 1.7) * 0.62);
    const dist = 3100;
    const sx = Math.cos(ang) * dist;
    const sy = elev * 2200 + 140;
    const sz = Math.sin(ang) * dist * 0.6;
    const target = controls.target;
    sun.position.set(target.x + sx, sy, target.z + sz);
    sun.target.position.copy(target);
    sun.target.updateMatrixWorld();
    // Cool rim from the anti-sun side, low: separates building tops/edges from the sky (day only).
    rim.position.set(target.x - sx * 0.7, sy * 0.35 + 300, target.z - sz * 0.7);
    rim.target.position.copy(target);
    rim.target.updateMatrixWorld();
    // Everything below is a pure function of f/nf (colours, emissive, bloom, exposure). It changes
    // only as the clock advances, so refresh it at ~15Hz instead of every frame — invisible over a
    // 120s day. (First call: lastToD is NaN, |f-NaN|<EPS is false, so the block runs.)
    if (Math.abs(f - lastToD) < 0.0005) return;
    lastToD = f;
    // colours
    const dusk = smoothstep(0.55, 0.78, f) * (1 - smoothstep(0.86, 0.98, f)) + smoothstep(0.1, 0.26, 1 - f) * 0; // warm near dusk
    tmpA.copy(COL.sunDay).lerp(COL.sunDusk, Math.min(1, dusk + smoothstep(0.18, 0.3, 1 - f) * 0));
    tmpA.lerp(COL.sunNight, nf);
    sun.color.copy(tmpA);
    // Stronger sun + weaker daytime hemi fill = a wider warm/cool ratio so unlit faces read as
    // genuinely shadowed instead of flat-lit (the biggest fix for post-shadow flatness).
    sun.intensity = 1.05 + (1 - nf) * 2.85;
    rim.intensity = 0.5 * (1 - nf); // day-only; never fights the night's cool moonlight silhouettes
    hemi.color.copy(tmpB.copy(COL.hemiSkyDay).lerp(COL.hemiSkyNight, nf));
    hemi.groundColor.copy(tmpB.copy(COL.hemiGndDay).lerp(COL.hemiGndNight, nf));
    hemi.intensity = 0.34 + nf * 1.2;
    (scene.background as THREE.Color).copy(tmpB.copy(COL.skyDay).lerp(COL.skyNight, nf));
    if (skyDome) (skyDome.material as THREE.MeshBasicMaterial).color.copy(tmpB.copy(COL.domeDay).lerp(COL.domeNight, nf));
    if (scene.fog) {
      (scene.fog as THREE.FogExp2).color.copy(scene.background as THREE.Color);
      // Heavier daytime haze adds aerial-perspective depth and hides the flat horizon; the night
      // term is lighter so the bloom-lit skyline stays crisp.
      (scene.fog as THREE.FogExp2).density = 0.00013 - nf * 0.00003;
    }
    // windows + ground streetlights glow at night
    const winI = nf * 0.7;
    for (const m of facadeMats) m.emissiveIntensity = winI;
    // Outskirts windows stay mostly dark so the bright core owns the night.
    for (const m of outerFacadeMats) m.emissiveIntensity = nf * 0.13;
    // Shopfronts + billboards: lit a little by day, neon-bright at night.
    for (const m of signMats) m.emissiveIntensity = 0.32 + nf * 0.95;
    // Hero-building lit windows: off by day, glowing at night.
    for (const m of heroEmitMats) m.emissiveIntensity = nf * 0.7;
    groundMat.emissiveIntensity = nf * 1.05;
    scene.environmentIntensity = 0.06 + (1 - nf) * 0.4; // sky reflections fade out at night
    for (const m of streetLampMats) m.emissiveIntensity = nf * 3.2;
    // night headlights (all car heads share one material)
    if (headMat) headMat.emissiveIntensity = nf * 1.1;
    if (renderer) renderer.toneMappingExposure = 1.14 + nf * 0.2 + dusk * 0.12;
    // bloom — dusk lets golden-hour highlights catch a little; day/night curves are unchanged.
    bloom.strength = 0.1 + nf * 0.18 + dusk * 0.06;
    bloom.threshold = 0.92 - nf * 0.14 - dusk * 0.06;
    bloom.radius = 0.3 + nf * 0.18;
    // drive the grade pass: golden-hour warmth + saturation lift, peaking at dusk (0 by day/night).
    if (gradePass) gradePass.material.uniforms.uDusk.value = dusk;
  }

  function step(dt: number) {
    for (const d of dealers) {
      if (d.state === 'rest') {
        if ((sold[d.bid] ?? 0) >= BATCH) {
          sold[d.bid] -= BATCH;
          const c = roadPoint();
          d.tx = c.x;
          d.ty = c.y;
          d.state = 'out';
          const cust = makeFigure(0xc05a5a, 1.5);
          cust.position.set(c.x, 0, c.y);
          agentGroup.add(cust);
          d.cust = cust;
        }
      } else if (d.state === 'out') {
        if (moveToward(d, dt)) {
          d.state = 'deal';
          d.timer = rand(0.8, 1.5);
        }
      } else if (d.state === 'deal') {
        d.timer -= dt;
        if (d.timer <= 0) {
          d.tx = d.home.x;
          d.ty = d.home.y;
          d.state = 'back';
          spawnRiser(d.x, d.y, '+' + money(Math.round(BATCH * SELL_PRICE * (1 + current.rep))), 'cash');
          d.cust && agentGroup.remove(d.cust);
          d.cust = null;
        }
      } else if (d.state === 'back') {
        if (moveToward(d, dt)) {
          d.state = 'rest';
          d.timer = rand(0.4, 1.5);
        }
      }
      d.g.position.set(d.x, 0, d.y);
    }
    for (const b of current.buildings) {
      if ((made[b.id] ?? 0) >= BATCH) {
        made[b.id] -= BATCH;
        const pp = propertyPosition(b);
        spawnRiser(pp.x, pp.z, '+product', 'grow');
      }
    }
    for (let i = 0; i < cars.length; i++) {
      const car = cars[i];
      const s = car.seg;
      const lane = s.major ? 7 : 4.5;
      car.t += car.dir * car.speed * dt;
      let px: number, py: number, pz: number, ang: number;
      if (s.horiz) {
        if (car.t > 1.05) car.t = -0.05;
        if (car.t < -0.05) car.t = 1.05;
        px = s.x1 + (s.x2 - s.x1) * car.t;
        py = 0;
        // shoreline avenue: keep the south lane on land instead of out over the harbour
        pz = Math.min(s.y1 + car.dir * lane, CAR_SHORE_Z);
        ang = car.dir > 0 ? 0 : Math.PI;
      } else {
        const bridge = BRIDGE_X.includes(s.x1);
        if (bridge) {
          if (car.t > 1.05) car.t = -0.05;
          if (car.t < -0.05) car.t = 1.05;
        } else {
          // non-bridge streets turn back before the waterfront instead of driving into the water
          if (car.t > CAR_SHORE_T) {
            car.t = CAR_SHORE_T;
            car.dir = -1;
          }
          if (car.t < -0.05) {
            car.t = -0.05;
            car.dir = 1;
          }
        }
        const z = s.y1 + (s.y2 - s.y1) * car.t;
        // ride up onto the deck as the avenue crosses the harbour
        py = bridge ? THREE.MathUtils.clamp((z - (WATER_Z0 - 70)) / 50, 0, 1) * (BRIDGE_DECK_Y + 1.5) : 0;
        px = s.x1 + car.dir * lane;
        pz = z;
        ang = car.dir > 0 ? -Math.PI / 2 : Math.PI / 2;
      }
      if (carBodyInst) {
        // group transform, then bake the body/head local offsets so both track rotation
        _agM.compose(_agP.set(px, py, pz), _agQ.setFromAxisAngle(_agY, ang), _agS);
        carBodyInst.setMatrixAt(i, _agT.multiplyMatrices(_agM, _carBodyOff));
        carHeadInst!.setMatrixAt(i, _agT.multiplyMatrices(_agM, _carHeadOff));
      }
    }
    if (carBodyInst) {
      carBodyInst.instanceMatrix.needsUpdate = true;
      carHeadInst!.instanceMatrix.needsUpdate = true;
    }
    for (let i = 0; i < peds.length; i++) {
      const p = peds[i];
      if (moveToward(p, dt)) {
        const t2 = roadPoint();
        p.tx = t2.x;
        p.ty = t2.y;
      }
      if (pedInst) pedInst.setMatrixAt(i, _agT.makeTranslation(p.x, 2.6, p.y));
    }
    if (pedInst) pedInst.instanceMatrix.needsUpdate = true;
    if (followDealer) {
      controls.target.lerp(_followTarget.set(followDealer.x, 0, followDealer.y), 0.08);
    }
  }

  // ---- HTML label / riser projection ----
  const proj = new THREE.Vector3();
  const _followTarget = new THREE.Vector3();
  const labelSlots: { x: number; y: number }[] = []; // reused across frames — no per-frame array
  function projectLabels() {
    const r = renderer!.domElement;
    const w = r.clientWidth,
      h = r.clientHeight;
    // Wide view shows only the empire that matters (owned / affordable / active / hovered).
    // Zoom in past the detail threshold to reveal every other for-sale plot. Keeps it uncluttered.
    const detail = camera.zoom > 1.05;
    labelSlots.length = 0;
    for (const m of orderedMarkers) {
      const hovered = m.el === hoveredEl;
      proj.set(m.x, m.y, m.z).project(camera);
      const sx = (proj.x * 0.5 + 0.5) * w;
      const sy = (-proj.y * 0.5 + 0.5) * h;
      // Hysteresis: a visible label needs clear overlap to hide, a hidden one needs clear
      // space to appear — kills the pop-in flicker while panning dense districts.
      const cw = m.shown ? 72 : 92;
      const ch = m.shown ? 29 : 39;
      const crowded = labelSlots.some((p) => Math.abs(p.x - sx) < cw && Math.abs(p.y - sy) < ch);
      const vis = (detail || m.important || hovered) && proj.z < 1 && (hovered || !crowded);
      if (vis !== m.shown) {
        m.el.classList.toggle('mhidden', !vis); // opacity/visibility fade, not a display pop
        m.shown = vis;
      }
      m.el.classList.toggle('hover', hovered && vis);
      if (vis) {
        labelSlots.push({ x: sx, y: sy });
        m.el.style.transform = `translate(-50%,-100%) translate(${sx}px,${sy}px)`;
      }
    }
    // District names guide the wide view; ramp them out as individual plots take over.
    const dOp = 0.62 * Math.max(0, Math.min(1, (1.4 - camera.zoom) / 0.3));
    for (const d of districtEls) {
      proj.set(d.x, 6, d.z).project(camera);
      const vis = dOp > 0.02 && proj.z < 1;
      d.el.style.display = vis ? 'block' : 'none';
      if (vis) {
        d.el.style.opacity = String(dOp);
        d.el.style.transform = `translate(-50%,-50%) translate(${(proj.x * 0.5 + 0.5) * w}px,${(-proj.y * 0.5 + 0.5) * h}px)`;
      }
    }
  }
  function projectRisers() {
    const r = renderer!.domElement;
    const w = r.clientWidth,
      h = r.clientHeight;
    risers = risers.filter((rs) => {
      rs.life -= 0.016;
      rs.y += 0.6;
      if (rs.life <= 0) {
        rs.el.remove();
        return false;
      }
      proj.set(rs.x, rs.y, rs.z).project(camera);
      rs.el.style.opacity = String(Math.max(0, rs.life));
      rs.el.style.transform = `translate(-50%,-100%) translate(${(proj.x * 0.5 + 0.5) * w}px,${(-proj.y * 0.5 + 0.5) * h}px)`;
      return true;
    });
  }

  function readCity(): City {
    let v: City;
    worldStore.subscribe((c) => (v = c))();
    return v!;
  }
  const buildKey = (c: City) => c.buildings.map((b) => b.id).join(',') + '|' + c.plots.map((p) => p.id + ':' + (c.cash >= p.cost ? 1 : 0)).join(',');

  onMount(() => {
    current = readCity();
    // antialias:false — we render through EffectComposer, so MSAA on the default framebuffer is
    // wasted (the final pass blits a fullscreen quad). Geometry AA comes from MSAA on the
    // composer's HDR targets instead (set below), which resolves edges BEFORE bloom.
    renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.25));
    renderer.setSize(wrap.clientWidth, wrap.clientHeight);
    // Cast shadows are OFF: the 1536² shadow map render (~120 draw calls) + per-fragment PCF
    // sampling were a real GPU cost on the wide-zoom worst case. GTAO still grounds the buildings
    // (contact AO at bases/corners), so the diorama keeps its depth without the shadow pass.
    renderer.shadowMap.enabled = false;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.18;
    maxAniso = renderer.capabilities.getMaxAnisotropy();
    wrap.insertBefore(renderer.domElement, labelHost);

    scene = new THREE.Scene();
    scene.background = new THREE.Color(0xd7cdb8);
    scene.fog = new THREE.FogExp2(0xd7cdb8, 0.00007);
    // Sky environment map → reflections on glass + subtle image-based ambient (intensity is
    // modulated by day/night in applyTimeOfDay so it never washes out the night).
    {
      const sky = document.createElement('canvas');
      sky.width = sky.height = 256;
      const s2 = sky.getContext('2d')!;
      const grd = s2.createLinearGradient(0, 0, 0, 256);
      grd.addColorStop(0.0, '#41618c');
      grd.addColorStop(0.46, '#9fb0c0');
      grd.addColorStop(0.53, '#d8c4a6');
      grd.addColorStop(1.0, '#2a2620');
      s2.fillStyle = grd;
      s2.fillRect(0, 0, 256, 256);
      const skyTex = new THREE.CanvasTexture(sky);
      skyTex.mapping = THREE.EquirectangularReflectionMapping;
      skyTex.colorSpace = THREE.SRGBColorSpace;
      const pmrem = new THREE.PMREMGenerator(renderer);
      scene.environment = pmrem.fromEquirectangular(skyTex).texture;
      scene.environmentIntensity = 0.35;
      skyTex.dispose();
      pmrem.dispose();
    }
    // Gradient sky backdrop (BackSide dome) so the horizon reads as sky, not a flat colour band.
    // One draw call, no lighting, no fog; day/night mood comes from the material.color tint. The
    // flat scene.background is kept only as the fog target colour behind it.
    {
      const dc = document.createElement('canvas');
      dc.width = 4;
      dc.height = 256;
      const dctx = dc.getContext('2d')!;
      const dg = dctx.createLinearGradient(0, 0, 0, 256);
      dg.addColorStop(0.0, '#8aa6ca'); // zenith
      dg.addColorStop(0.5, '#c3cbd4');
      dg.addColorStop(0.74, '#ecdcbe'); // warm horizon band
      dg.addColorStop(1.0, '#dccdb0');
      dctx.fillStyle = dg;
      dctx.fillRect(0, 0, 4, 256);
      const domeTex = new THREE.CanvasTexture(dc);
      domeTex.colorSpace = THREE.SRGBColorSpace;
      const domeMat = new THREE.MeshBasicMaterial({ map: domeTex, side: THREE.BackSide, fog: false, depthWrite: false, toneMapped: true });
      skyDome = new THREE.Mesh(new THREE.SphereGeometry(7000, 24, 12), domeMat);
      skyDome.position.set(WORLD / 2, 0, WORLD / 2);
      skyDome.renderOrder = -1;
      scene.add(skyDome);
    }

    const aspect = wrap.clientWidth / wrap.clientHeight || 1;
    camera = new THREE.OrthographicCamera((-viewSize * aspect) / 2, (viewSize * aspect) / 2, viewSize / 2, -viewSize / 2, -3000, 9000);
    // establishing view: centred on the city, tilted diorama angle
    const tx = WORLD / 2;
    const tz = WORLD / 2;
    camera.position.set(tx + 1200, 2000, tz + 1200);
    camera.zoom = 0.82;
    camera.updateProjectionMatrix();

    controls = new MapControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.09;
    controls.screenSpacePanning = false;
    controls.minPolarAngle = 0.18 * Math.PI;
    controls.maxPolarAngle = 0.31 * Math.PI;
    const baseAzimuth = Math.PI / 4;
    const azimuthSweep = Math.PI / 10;
    controls.minAzimuthAngle = baseAzimuth - azimuthSweep;
    controls.maxAzimuthAngle = baseAzimuth + azimuthSweep;
    controls.minZoom = 0.05; // recalculated from viewport + camera angle by clampMapView()
    controls.maxZoom = 3.2;
    controls.target.set(tx, 0, tz);
    controls.update();

    // lighting
    hemi = new THREE.HemisphereLight(0xdfe6e6, 0x9c8b6a, 0.5);
    scene.add(hemi);
    sun = new THREE.DirectionalLight(0xfff0d2, 2.6);
    // sun.castShadow stays false — shadows removed for wide-zoom GPU headroom (GTAO grounds instead).
    scene.add(sun, sun.target);
    // Cheap cool rim (one extra N·L term, no shadow map) added from the anti-sun side to pop edges.
    rim = new THREE.DirectionalLight(0x9fc0e6, 0);
    scene.add(rim, rim.target);

    // ground
    const gtex = new THREE.CanvasTexture(drawGround());
    gtex.colorSpace = THREE.SRGBColorSpace;
    gtex.anisotropy = maxAniso;
    const gglow = new THREE.CanvasTexture(drawGroundGlow());
    gglow.colorSpace = THREE.SRGBColorSpace;
    // Slightly glossier ground picks up a faint warm sun-side sheen (a cheap fake for the lost cast
    // shadows' light direction) plus a touch of cool sky bounce via env reflection.
    groundMat = new THREE.MeshStandardMaterial({ map: gtex, emissiveMap: gglow, emissive: 0xffb060, emissiveIntensity: 0, roughness: 0.88, metalness: 0, envMapIntensity: 0.35 });
    const ground = new THREE.Mesh(new THREE.PlaneGeometry(WORLD, WORLD), groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(WORLD / 2, 0, WORLD / 2);
    ground.receiveShadow = true;
    scene.add(ground);
    // Non-playable procedural outskirts hide the square city edge at wide zooms.
    const outerTex = new THREE.CanvasTexture(drawOutskirts());
    outerTex.colorSpace = THREE.SRGBColorSpace;
    outerTex.anisotropy = Math.min(4, maxAniso);
    const skirt = new THREE.Mesh(new THREE.PlaneGeometry(OUTER_SIZE, OUTER_SIZE), new THREE.MeshStandardMaterial({ map: outerTex, roughness: 1 }));
    skirt.rotation.x = -Math.PI / 2;
    skirt.position.set(WORLD / 2, -1.5, WORLD / 2);
    skirt.receiveShadow = true;
    scene.add(skirt);

    // --- harbour: animated water + piers + boats ---
    const waterZ0 = GY[GY.length - 2];
    const wD = WORLD - waterZ0;
    const wGeo = new THREE.PlaneGeometry(WORLD, wD, 48, 10);
    wGeo.rotateX(-Math.PI / 2);
    // Near-dielectric, deeper body + boosted env reflection so the warm sky reads as a crisp golden
    // streak on the water instead of averaging to metallic grey.
    const waterMat = new THREE.MeshStandardMaterial({ color: 0x18384a, roughness: 0.12, metalness: 0.04, envMapIntensity: 1.6 });
    waterMat.onBeforeCompile = (shader) => {
      waterTimeUniform = { value: 0 };
      shader.uniforms.uWaterTime = waterTimeUniform;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uWaterTime;')
        .replace(
          '#include <beginnormal_vertex>',
          `float waveDx = 0.045 * cos(position.x * 0.03 + uWaterTime * 1.3);
           float waveDz = -0.06 * sin(position.z * 0.05 + uWaterTime * 0.9);
           vec3 objectNormal = normalize(vec3(-waveDx, 1.0, -waveDz));`
        )
        .replace(
          '#include <begin_vertex>',
          `vec3 transformed = vec3(position);
           transformed.y += sin(position.x * 0.03 + uWaterTime * 1.3) * 1.5
             + cos(position.z * 0.05 + uWaterTime * 0.9) * 1.2;`
        );
    };
    waterMat.customProgramCacheKey = () => 'cloud-nine-water-v1';
    water = new THREE.Mesh(wGeo, waterMat);
    water.position.set(WORLD / 2, 1.8, (waterZ0 + WORLD) / 2);
    water.receiveShadow = true;
    scene.add(water);
    // Static harbour + bridge geometry is instanced by (geometry, material): piers, and each bridge
    // part (deck / centre-line / rails / pylons / lamp-poles / bulbs). ~50 meshes → ~7 draws.
    const pierMat = new THREE.MeshStandardMaterial({ color: 0x6b5538, roughness: 0.9 });
    const hullMat = new THREE.MeshStandardMaterial({ color: 0xb8b2a4, roughness: 0.7 });
    const cabinMat = new THREE.MeshStandardMaterial({ color: 0x445a6b, roughness: 0.6 });
    const deckMat = new THREE.MeshStandardMaterial({ color: 0x37322e, roughness: 0.92 });
    const railMat = new THREE.MeshStandardMaterial({ color: 0x5c4a33, roughness: 0.85 });
    const lineMat = new THREE.MeshStandardMaterial({ color: 0xe9c863, roughness: 0.7 });
    const bridgeLampMat = new THREE.MeshStandardMaterial({ color: 0xffd391, emissive: 0xffa33b, emissiveIntensity: 0 });
    streetLampMats.push(bridgeLampMat);
    const bSpan = WORLD - waterZ0;
    const bCz = (waterZ0 + WORLD) / 2;
    const deckLen = bSpan + 46; // overhang onto both banks so the road meets the deck
    const deckW = 42;
    const trM = (x: number, y: number, z: number) => new THREE.Matrix4().makeTranslation(x, y, z);
    const pierMx: THREE.Matrix4[] = [];
    const deckMx: THREE.Matrix4[] = [], clMx: THREE.Matrix4[] = [], railMx: THREE.Matrix4[] = [];
    const pylMx: THREE.Matrix4[] = [], poleMx: THREE.Matrix4[] = [], bulbMx: THREE.Matrix4[] = [];
    for (let i = 0; i < 3; i++) pierMx.push(trM(WORLD * (0.22 + i * 0.3), 3, waterZ0 + 32));
    for (const bx of BRIDGE_X) {
      deckMx.push(trM(bx, BRIDGE_DECK_Y, bCz));
      clMx.push(trM(bx, BRIDGE_DECK_Y + 1.7, bCz));
      for (const side of [-1, 1]) {
        railMx.push(trM(bx + side * (deckW / 2 - 1.2), BRIDGE_DECK_Y + 3.2, bCz));
        for (const pz of [bCz - bSpan * 0.26, bCz + bSpan * 0.26]) pylMx.push(trM(bx + side * (deckW / 2 - 5), -3, pz));
        for (const lz of [bCz - bSpan / 2 + 16, bCz + bSpan / 2 - 16]) {
          poleMx.push(trM(bx + side * (deckW / 2 - 3), BRIDGE_DECK_Y + 9, lz));
          bulbMx.push(trM(bx + side * (deckW / 2 - 3), BRIDGE_DECK_Y + 18, lz));
        }
      }
    }
    const staticInst = (geo: THREE.BufferGeometry, mat: THREE.Material, arr: THREE.Matrix4[]) => {
      const im = new THREE.InstancedMesh(geo, mat, arr.length);
      im.instanceMatrix.setUsage(THREE.StaticDrawUsage);
      im.receiveShadow = true;
      arr.forEach((m, i) => im.setMatrixAt(i, m));
      im.instanceMatrix.needsUpdate = true;
      scene.add(im);
    };
    staticInst(new THREE.BoxGeometry(10, 5, 74), pierMat, pierMx);
    staticInst(new THREE.BoxGeometry(deckW, 3, deckLen), deckMat, deckMx);
    staticInst(new THREE.BoxGeometry(1.6, 0.4, deckLen - 24), lineMat, clMx);
    staticInst(new THREE.BoxGeometry(2.2, 4.5, deckLen), railMat, railMx);
    staticInst(new THREE.BoxGeometry(5.5, 16, 5.5), pierMat, pylMx);
    staticInst(new THREE.CylinderGeometry(1, 1.3, 18, 6), railMat, poleMx);
    staticInst(new THREE.SphereGeometry(2.4, 8, 6), bridgeLampMat, bulbMx);

    // Boats: instanced hull + cabin, animated per frame in the loop (matrices only).
    boatHullInst = new THREE.InstancedMesh(new THREE.BoxGeometry(34, 8, 13), hullMat, 4);
    boatCabinInst = new THREE.InstancedMesh(new THREE.BoxGeometry(14, 8, 9), cabinMat, 4);
    boatHullInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    boatCabinInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    boatHullInst.frustumCulled = false;
    boatCabinInst.frustumCulled = false;
    scene.add(boatHullInst, boatCabinInst);
    for (let i = 0; i < 4; i++) {
      boats.push({ x: Math.random() * WORLD, z: waterZ0 + 55 + Math.random() * (wD - 85), dir: Math.random() < 0.5 ? 1 : -1, speed: 16 + Math.random() * 18, phase: Math.random() * 6 });
    }

    // Buildings are grouped into six instanced material batches. This turns hundreds
    // of individual skyline meshes/materials into a small, stable set of draw calls.
    type SceneryBox = { x: number; y: number; z: number; w: number; h: number; d: number; tone: number };
    const unitBox = new THREE.BoxGeometry(1, 1, 1);
    const tmpM = new THREE.Matrix4();
    const tone = new THREE.Color();
    // Core footprints are placed from the authored NPC kit (instanced once the glbs load).
    type KitPlace = { model: number; cx: number; cz: number; w: number; d: number; h: number; rot: number };
    const kitPlace: KitPlace[] = [];
    const krnd = mulberry32(0x9a1c0de5);
    // Taller buildings + a narrower-footprint nudge so wide lots stop reading as shallow slabs.
    const bldgHeight = (h: number) => h * 1.6 + 10;
    const pickKit = (f: Foot, r: number) => {
      if (f.zone === 'docks') return r < 0.6 ? 7 : 4; // small warehouse / block
      if (f.height > 58) return [6, 5, 8, 10, 11][(r * 5) | 0]; // office / apartment / ltower / podtower / terrace
      if (f.height > 34) return [2, 4, 1, 9, 11][(r * 5) | 0]; // midrise / block / rowhouse / lshape / terrace
      if (f.zone === 'residential') return r < 0.5 ? 0 : 1; // house / rowhouse
      return r < 0.5 ? 3 : 1; // shop / rowhouse
    };
    for (const f of footprints) {
      const r = krnd();
      // gently cap very wide footprints so a single building isn't an extreme slab
      const w = Math.min(f.w, 58 + f.h * 0.55);
      const d = Math.min(f.h, 58 + f.w * 0.55);
      kitPlace.push({ model: pickKit(f, r), cx: f.x + f.w / 2, cz: f.y + f.h / 2, w, d, h: bldgHeight(f.height), rot: w >= d ? 0 : Math.PI / 2 });
    }
    // Outskirts get their OWN darker, near-unlit instanced batches (still six material
    // groups, still instanced) so the playable core reads as the bright, important centre.
    const outerBoxesByKind: SceneryBox[][] = KINDS.map(() => []);
    for (const f of outerFootprints) {
      outerBoxesByKind[f.kind].push({ x: f.x + f.w / 2, y: f.height / 2, z: f.y + f.h / 2, w: f.w, h: f.height, d: f.h, tone: f.tone });
    }
    const outerRoofMats = KINDS.map((k) => new THREE.MeshStandardMaterial({ color: hexShade(k.roof, 0.6), roughness: 0.98 }));
    for (let kind = 0; kind < KINDS.length; kind++) {
      const boxes = outerBoxesByKind[kind];
      if (!boxes.length) continue;
      const k = KINDS[kind];
      const tex = facadeTex(kind);
      const facadeMat = new THREE.MeshStandardMaterial({
        map: tex.color,
        emissiveMap: tex.emissive,
        emissive: new THREE.Color(k.win),
        emissiveIntensity: 0,
        color: 0x868c95, // cool, dark multiply darkens + desaturates the baked facade ~45%
        vertexColors: true,
        normalMap: tex.normal,
        normalScale: new THREE.Vector2(0.5, 0.5),
        roughness: 0.97,
        metalness: 0,
      });
      outerFacadeMats.push(facadeMat);
      const mats = [facadeMat, facadeMat, outerRoofMats[kind], outerRoofMats[kind], facadeMat, facadeMat];
      const inst = new THREE.InstancedMesh(unitBox, mats, boxes.length);
      inst.instanceMatrix.setUsage(THREE.StaticDrawUsage);
      inst.receiveShadow = false;
      boxes.forEach((b, i) => {
        tmpM.makeScale(b.w, b.h, b.d);
        tmpM.setPosition(b.x, b.y, b.z);
        inst.setMatrixAt(i, tmpM);
        tone.setRGB(b.tone, b.tone, b.tone);
        inst.setColorAt(i, tone);
      });
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      scene.add(inst);
    }
    // (core skyline + rooftop grit are now provided by the instanced NPC kit — see buildKitCity)
    // instanced trees (parks + avenues)
    {
      const all = [...parkTrees, ...streetTrees, ...outerTrees, ...padTrees];
      if (all.length) {
        const trunk = new THREE.CylinderGeometry(1.1, 1.6, 8, 5).toNonIndexed();
        trunk.translate(0, 4, 0);
        const foliage = new THREE.IcosahedronGeometry(6.5, 0);
        foliage.translate(0, 12.5, 0);
        setGeoColor(trunk, 0x5a4030);
        setGeoColor(foliage, 0x4c6531);
        const treeGeo = mergeGeometries([trunk, foliage], false);
        const treeMat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.92, flatShading: true });
        const inst = new THREE.InstancedMesh(treeGeo, treeMat, all.length);
        const m = new THREE.Matrix4();
        all.forEach((t, i) => {
          m.makeScale(t.s, t.s + (i % 3) * 0.12, t.s);
          m.setPosition(t.x, 0, t.z);
          inst.setMatrixAt(i, m);
        });
        scene.add(inst);
      }
    }
    addStreetDetails();

    // district labels (as floating HTML)
    for (const [i, d] of districts.entries()) {
      const el = document.createElement('div');
      el.className = 'district d' + i;
      el.textContent = d.t;
      labelHost.appendChild(el);
      districtEls.push({ el, x: wpx(d.x), z: wpx(d.y) });
    }

    // agent shared assets
    markerGroup = new THREE.Group();
    agentGroup = new THREE.Group();
    scene.add(markerGroup, agentGroup);
    ringGeo = new THREE.RingGeometry(0.94, 1.0, 48);
    hitGeo = new THREE.CylinderGeometry(34, 34, 120, 8);
    hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    // Two shared ring materials (owned=green / for-sale=amber). Previously addMarker created a
    // fresh material per marker, leaked on every syncBuildings() rebuild (Group.clear disposes nothing).
    ringMatOwned = new THREE.MeshBasicMaterial({ color: 0x39e070, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });
    ringMatForSale = new THREE.MeshBasicMaterial({ color: 0xf3b54a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false });
    ringMatOwnedHover = new THREE.MeshBasicMaterial({ color: 0x6effa0, transparent: true, opacity: 1, side: THREE.DoubleSide, depthWrite: false });
    ringMatForSaleHover = new THREE.MeshBasicMaterial({ color: 0xffd27a, transparent: true, opacity: 1, side: THREE.DoubleSide, depthWrite: false });
    ownedTintGeo = new THREE.CircleGeometry(1, 40);
    {
      const tintCv = document.createElement('canvas');
      tintCv.width = tintCv.height = 128;
      const tctx = tintCv.getContext('2d')!;
      const tgrad = tctx.createRadialGradient(64, 64, 0, 64, 64, 64);
      tgrad.addColorStop(0, 'rgba(70,224,130,0.5)');
      tgrad.addColorStop(0.6, 'rgba(60,200,116,0.22)');
      tgrad.addColorStop(1, 'rgba(60,200,116,0)');
      tctx.fillStyle = tgrad;
      tctx.fillRect(0, 0, 128, 128);
      const tintTex = new THREE.CanvasTexture(tintCv);
      tintTex.colorSpace = THREE.SRGBColorSpace;
      ownedTintMat = new THREE.MeshBasicMaterial({ map: tintTex, transparent: true, opacity: 0.85, depthWrite: false });
    }
    carGeo = new THREE.BoxGeometry(14, 5, 7);
    carHeadGeo = new THREE.BoxGeometry(1.5, 2, 6);
    pedGeo = new THREE.CylinderGeometry(1.5, 1.5, 5, 6);
    headMat = new THREE.MeshStandardMaterial({ color: 0x222222, emissive: 0xfff2c8, emissiveIntensity: 0, roughness: 0.5 });
    pedMatA = new THREE.MeshStandardMaterial({ color: 0x5a5560, roughness: 0.8 });
    // Instanced agent draws (matrices rewritten each frame in step()). frustumCulled off so a
    // stale instance bounding-sphere can't cull the whole layer as agents roam the map.
    pedInst = new THREE.InstancedMesh(pedGeo, pedMatA, 80);
    pedInst.frustumCulled = false;
    pedInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // white base so per-instance colour (instanceColor multiplies) reproduces the 7 CARCOLORS
    const carBodyMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.5, metalness: 0.3 });
    carBodyInst = new THREE.InstancedMesh(carGeo, carBodyMat, 40);
    carBodyInst.frustumCulled = false;
    carBodyInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    carHeadInst = new THREE.InstancedMesh(carHeadGeo, headMat, 40);
    carHeadInst.frustumCulled = false;
    carHeadInst.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    agentGroup.add(pedInst, carBodyInst, carHeadInst);

    syncBuildings();
    // Load authored CC0 hero models; swap them in for the procedural plot buildings as they arrive.
    {
      const gltf = new GLTFLoader();
      const arts: BuildingArt[] = ['corner', 'stashhouse', 'bodega', 'garage', 'tenement', 'loft', 'motel', 'warehouse', 'factory', 'tower'];
      for (const art of arts) {
        gltf.load(
          `${import.meta.env.BASE_URL}models/${art}.glb`,
          (g) => {
            if (destroyed) return;
            const root = g.scene;
            root.traverse((o) => {
              const mesh = o as THREE.Mesh;
              if (mesh.isMesh) {
                mesh.castShadow = true;
                mesh.receiveShadow = true;
                const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                for (const mm of mats) {
                  const sm = mm as THREE.MeshStandardMaterial;
                  if (sm && sm.emissiveMap) {
                    sm.emissive.setRGB(1, 1, 1);
                    sm.emissiveIntensity = 0;
                    heroEmitMats.add(sm); // lit windows: dark by day, glow at night
                  }
                }
              }
            });
            const size = new THREE.Vector3();
            new THREE.Box3().setFromObject(root).getSize(size);
            heroModels[art] = { scene: root, size };
            if (scene) syncBuildings();
          },
          undefined,
          (err) => console.warn('hero model failed to load:', art, err)
        );
      }
    }
    // ---- NPC kit: load reusable building models, instance them across the core footprints ----
    {
      const kitGroup = new THREE.Group();
      const kitNames = ['npc_house', 'npc_rowhouse', 'npc_midrise', 'npc_shop', 'npc_block', 'npc_apartment', 'npc_office', 'npc_warehouse_sm', 'npc_ltower', 'npc_lshape', 'npc_podtower', 'npc_terrace'];
      type KitModel = { size: THREE.Vector3; prims: { geo: THREE.BufferGeometry; mat: THREE.Material; matrix: THREE.Matrix4 }[] };
      const kitModels: (KitModel | null)[] = kitNames.map(() => null);
      let kitDone = 0;
      const buildKitCity = () => {
        if (destroyed) return;
        const byModel: KitPlace[][] = kitNames.map(() => []);
        for (const p of kitPlace) (byModel[p.model] ?? byModel[0]).push(p);
        const M = new THREE.Matrix4(), q = new THREE.Quaternion(), pos = new THREE.Vector3(), scl = new THREE.Vector3(), yA = new THREE.Vector3(0, 1, 0);
        const col = new THREE.Color();
        for (let mi = 0; mi < kitModels.length; mi++) {
          const km = kitModels[mi];
          const places = byModel[mi];
          if (!km || !places.length) continue;
          for (const prim of km.prims) {
            const inst = new THREE.InstancedMesh(prim.geo, prim.mat, places.length);
            inst.castShadow = true;
            inst.receiveShadow = true;
            inst.instanceMatrix.setUsage(THREE.StaticDrawUsage);
            places.forEach((p, i) => {
              const sx = (p.rot === 0 ? p.w : p.d) / Math.max(0.01, km.size.x);
              const sz = (p.rot === 0 ? p.d : p.w) / Math.max(0.01, km.size.z);
              const sy = p.h / Math.max(0.01, km.size.y);
              q.setFromAxisAngle(yA, p.rot);
              M.compose(pos.set(p.cx, 0, p.cz), q, scl.set(sx, sy, sz));
              M.multiply(prim.matrix);
              inst.setMatrixAt(i, M);
              // deterministic per-building tint (brightness + warm/cool) so the kit varies a lot
              const hb = (((p.cx * 71 + p.cz * 31) | 0) % 100) / 100;
              const hc = (((p.cx * 37 + p.cz * 91) | 0) % 100) / 100;
              // Wider brightness AND a stronger warm↔cool hue split so adjacent buildings (and their
              // roofs) separate into weathered-warm terracotta vs. cool slate instead of one flat tan.
              const t = 0.58 + hb * 0.72;
              col.setRGB(t * (0.90 + hc * 0.24), t * (0.94 + (1 - hc) * 0.1), t * (0.80 + (1 - hc) * 0.3));
              inst.setColorAt(i, col);
            });
            inst.instanceMatrix.needsUpdate = true;
            if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
            kitGroup.add(inst);
          }
        }
        scene.add(kitGroup);
      };
      const kgltf = new GLTFLoader();
      for (let i = 0; i < kitNames.length; i++) {
        const idx = i;
        kgltf.load(
          `${import.meta.env.BASE_URL}models/${kitNames[idx]}.glb`,
          (g) => {
            g.scene.updateMatrixWorld(true);
            const size = new THREE.Vector3();
            new THREE.Box3().setFromObject(g.scene).getSize(size);
            const prims: KitModel['prims'] = [];
            g.scene.traverse((o) => {
              const mesh = o as THREE.Mesh;
              if (mesh.isMesh) {
                prims.push({ geo: mesh.geometry, mat: mesh.material as THREE.Material, matrix: mesh.matrixWorld.clone() });
                const sm = mesh.material as THREE.MeshStandardMaterial;
                if (sm && sm.emissiveMap) {
                  sm.emissive.setRGB(1, 1, 1);
                  sm.emissiveIntensity = 0;
                  heroEmitMats.add(sm);
                }
              }
            });
            kitModels[idx] = { size, prims };
            if (++kitDone === kitNames.length) buildKitCity();
          },
          undefined,
          (err) => {
            console.warn('kit model failed:', kitNames[idx], err);
            if (++kitDone === kitNames.length) buildKitCity();
          }
        );
      }
    }
    // ---- rich rooftop detail (the diorama looks straight down, so roofs carry the scene) ----
    {
      const rr = mulberry32(0x51eed00f);
      const V = THREE.Vector3, Q = new THREE.Quaternion(), yA = new THREE.Vector3(0, 1, 0);
      const acXf: THREE.Matrix4[] = [], tankXf: THREE.Matrix4[] = [], ventXf: THREE.Matrix4[] = [], stairXf: THREE.Matrix4[] = [], ductXf: THREE.Matrix4[] = [];
      const bb: { m: THREE.Matrix4; v: number }[] = [];
      const comp = (px: number, py: number, pz: number, sx: number, sy: number, sz: number, ry = 0) => {
        Q.setFromAxisAngle(yA, ry);
        return new THREE.Matrix4().compose(new V(px, py, pz), Q, new V(sx, sy, sz));
      };
      for (const f of footprints) {
        if (f.w < 18 || f.h < 18) continue;
        const y = bldgHeight(f.height); // props sit on the (taller) building top
        const cx = f.x + f.w / 2, cz = f.y + f.h / 2;
        const cw = Math.min(f.w, 58 + f.h * 0.55), cd = Math.min(f.h, 58 + f.w * 0.55); // matches building footprint
        const rx = () => cx - cw / 2 + 6 + rr() * (cw - 12);
        const rz = () => cz - cd / 2 + 6 + rr() * (cd - 12);
        // (rr() is consumed here to keep the prop scatter identical to before the roof deck was
        // removed — the deck read as a dark plate floating over the kit models' own roofs.)
        rr();
        const nac = 2 + ((rr() * 4) | 0);
        for (let i = 0; i < nac; i++) {
          const w = 3 + rr() * 4, d = 3 + rr() * 4, h = 1.8 + rr() * 2.4;
          acXf.push(comp(rx(), y + h / 2, rz(), w, h, d));
        }
        for (let i = 0; i < 1 + ((rr() * 3) | 0); i++) {
          const h = 3.5 + rr() * 4;
          ventXf.push(comp(rx(), y + h / 2, rz(), 1.5, h, 1.5));
        }
        if (rr() < 0.4) {
          const h = 6 + rr() * 4;
          tankXf.push(comp(rx(), y + h / 2, rz(), 5, h, 5));
        }
        if (rr() < 0.55 && cw > 38 && cd > 38) {
          const w = 8 + rr() * 9, d = 8 + rr() * 9, h = 5 + rr() * 4;
          stairXf.push(comp(cx + (rr() - 0.5) * 8, y + h / 2, cz + (rr() - 0.5) * 8, w, h, d));
        }
        if (rr() < 0.5) {
          const len = 8 + rr() * Math.min(cw, cd) * 0.5;
          const horiz = rr() < 0.5;
          ductXf.push(comp(rx(), y + 1.0, rz(), horiz ? len : 1.6, 1.6, horiz ? 1.6 : len));
        }
        // rooftop billboard: rarer, taller blocks only, on an edge facing out (4 ad designs)
        if (f.height > 52 && rr() < 0.12) {
          const sides = [[1, 0, Math.PI / 2], [-1, 0, -Math.PI / 2], [0, 1, 0], [0, -1, Math.PI]];
          const s = sides[(rr() * 4) | 0];
          const bw = Math.min(38, Math.min(cw, cd) * 0.8), bh = 12 + rr() * 8;
          bb.push({ m: comp(cx + s[0] * (cw / 2 - 4), y + bh / 2 + 2, cz + s[1] * (cd / 2 - 4), bw, bh, 1, s[2]), v: (rr() * 4) | 0 });
        }
      }
      const addInst = (geo: THREE.BufferGeometry, mat: THREE.Material, arr: THREE.Matrix4[], cast = true) => {
        if (!arr.length) return;
        const im = new THREE.InstancedMesh(geo, mat, arr.length);
        im.castShadow = cast;
        im.receiveShadow = true;
        arr.forEach((m, i) => im.setMatrixAt(i, m));
        im.instanceMatrix.needsUpdate = true;
        scene.add(im);
      };
      // (The tar/gravel roof-deck layer was removed — the authored kit models already carry their
      // own roofs, and the deck read as a dark plate floating just above them. Props below still
      // scatter across those roofs.)
      addInst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0x9ba0a4, roughness: 0.7, metalness: 0.25 }), acXf);
      addInst(new THREE.CylinderGeometry(0.5, 0.5, 1, 10), new THREE.MeshStandardMaterial({ color: 0x6b5340, roughness: 0.85 }), tankXf);
      addInst(new THREE.CylinderGeometry(0.5, 0.5, 1, 8), new THREE.MeshStandardMaterial({ color: 0x33373a, roughness: 0.6, metalness: 0.5 }), ventXf);
      addInst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0x7c776e, roughness: 0.9 }), stairXf);
      addInst(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshStandardMaterial({ color: 0x55595c, roughness: 0.7, metalness: 0.3 }), ductXf);
      if (bb.length) {
        const pal = [['#ff4fa3', '#46c8ff', '#ffd86b'], ['#7cff6b', '#ff7a59', '#ffffff'], ['#46c8ff', '#ffffff', '#ff4fa3'], ['#ffd86b', '#ff4fa3', '#7cff6b']];
        const plane = new THREE.PlaneGeometry(1, 1);
        for (let v = 0; v < 4; v++) {
          const list = bb.filter((b) => b.v === v);
          if (!list.length) continue;
          const ac = document.createElement('canvas');
          ac.width = 128; ac.height = 64;
          const ax = ac.getContext('2d')!;
          ax.fillStyle = '#16121c'; ax.fillRect(0, 0, 128, 64);
          ax.fillStyle = pal[v][0]; ax.fillRect(8, 10, 64, 12);
          ax.fillStyle = pal[v][1]; ax.fillRect(8, 30, 92, 10);
          ax.fillStyle = pal[v][2]; ax.fillRect(8, 46, 46, 9);
          const at = new THREE.CanvasTexture(ac); at.colorSpace = THREE.SRGBColorSpace;
          const bm = new THREE.MeshStandardMaterial({ map: at, emissiveMap: at, emissive: 0xffffff, emissiveIntensity: 0.35, side: THREE.DoubleSide, roughness: 0.6 });
          signMats.push(bm);
          const im = new THREE.InstancedMesh(plane, bm, list.length);
          list.forEach((b, i) => im.setMatrixAt(i, b.m));
          scene.add(im);
        }
      }
    }
    for (let i = 0; i < 40; i++) spawnCar(segs[(Math.random() * segs.length) | 0]);
    for (let i = 0; i < 80; i++) spawnPed();

    // post-processing (bloom)
    composer = new EffectComposer(renderer);
    // MSAA on the HDR ping-pong targets resolves building silhouette edges BEFORE UnrealBloom, so
    // bright/emissive edges stop crawling. samples=2 is deliberate: on HalfFloat targets, x4 costs
    // ~19fps at wide zoom (erasing the draw-call win) while x2 costs only ~5 and still cleans the
    // isometric silhouettes. .samples survives composer.setSize() and clamps to the GPU's maxSamples.
    composer.renderTarget1.samples = 2;
    composer.renderTarget2.samples = 2;
    composer.addPass(new RenderPass(scene, camera));
    // Ground-truth ambient occlusion: shades building bases, window recesses, street corners.
    // Computed at half resolution (cheap) and blended back at full res.
    gtao = new GTAOPass(scene, camera, Math.max(1, wrap.clientWidth * 0.5), Math.max(1, wrap.clientHeight * 0.5));
    gtao.output = GTAOPass.OUTPUT.Default;
    // Full blend + tighter radius: with cast shadows gone, GTAO is the main grounding cue, so a
    // darker, tighter base crease reads as a contact shadow that anchors buildings to the ground.
    gtao.blendIntensity = 1.0;
    gtao.updateGtaoMaterial({ radius: 18, distanceExponent: 1.4, thickness: 8, scale: 1, samples: 8, distanceFallOff: 1, screenSpaceRadius: false });
    composer.addPass(gtao);
    bloom = new UnrealBloomPass(new THREE.Vector2(wrap.clientWidth * 0.5, wrap.clientHeight * 0.5), 0.3, 0.6, 0.9);
    composer.addPass(bloom);
    composer.addPass(new OutputPass());
    smaaPass = new SMAAPass(); // crisper edges than MSAA alone (post-bloom neon/water shimmer)
    composer.addPass(smaaPass);
    // filmic colour grade (last pass): filmic contrast + saturation + warm/teal split-tone, with a
    // dusk term (uDusk, 0 by day & night) that warms + enriches golden hour without touching the
    // neutral midday or the crisp night window-glow.
    gradePass = new ShaderPass({
      uniforms: { tDiffuse: { value: null }, uContrast: { value: 1.12 }, uSat: { value: 1.14 }, uDusk: { value: 0 } },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
      fragmentShader: `
          varying vec2 vUv; uniform sampler2D tDiffuse; uniform float uContrast; uniform float uSat; uniform float uDusk;
          void main(){
            vec3 c = texture2D(tDiffuse, vUv).rgb;
            c = (c - 0.5) * uContrast + 0.5;
            float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
            c = mix(vec3(l), c, uSat + uDusk * 0.10);
            // cooler/deeper shadows + warmer highlights; dusk pushes the whole split warmer.
            vec3 split = vec3(0.04, 0.012, -0.042) + uDusk * vec3(0.055, 0.018, -0.05);
            c += (l - 0.5) * split;
            c *= mix(vec3(1.0), vec3(1.045, 1.005, 0.95), uDusk); // gentle golden mid-lift at dusk
            gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
          }`,
    });
    composer.addPass(gradePass);

    frustum();
    clampMapView();

    // Apply the saved graphics preset now and on every change (subscribe fires immediately).
    gfxUnsub = gfxQuality.subscribe((q) => applyGfx(q));

    const dom = renderer.domElement;
    dom.addEventListener('pointerdown', onDown);
    dom.addEventListener('pointermove', onMove);
    dom.addEventListener('pointerup', onUp);
    dom.addEventListener('contextmenu', onCtx);

    ro = new ResizeObserver(() => {
      if (!renderer) return;
      const w = wrap.clientWidth,
        h = wrap.clientHeight;
      if (w === 0 || h === 0) return; // hidden (building view) — keep the last real size, don't 0 it
      renderer.setSize(w, h);
      composer.setSize(w, h);
      gtao.setSize(Math.max(1, w * 0.5), Math.max(1, h * 0.5));
      bloom.setSize(Math.max(1, w * 0.5), Math.max(1, h * 0.5));
      frustum();
      // repaint immediately — resizing clears the WebGL buffer, and without this the browser can
      // paint that blank buffer before the next rAF tick re-renders it (a one-frame black flash).
      if (!paused) composer.render();
      clampDirty = true;
      labelsDirty = true;
    });
    ro.observe(wrap);

    let last = performance.now();
    const loop = () => {
      if (destroyed) return;
      raf = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (paused) return; // hidden behind the building view — keep rAF armed, do no work
      // controls.update() returns true while the camera is moving/damping; the map-view clamp
      // (4 raycasts + bounds) only needs to run then, when following a dealer, or after a resize.
      const camMoved = controls.update();
      if (camMoved || followDealer || clampDirty) {
        clampMapView();
        clampDirty = false;
      }
      step(dt);
      applyTimeOfDay(current.t);
      // environment animation
      const ts = now * 0.001;
      if (waterTimeUniform) waterTimeUniform.value = ts;
      for (let i = 0; i < boats.length; i++) {
        const b = boats[i];
        b.x += b.dir * b.speed * dt;
        if (b.x > WORLD + 50) b.x = -50;
        if (b.x < -50) b.x = WORLD + 50;
        const by = 3.4 + Math.sin(ts * 1.2 + b.phase) * 1.1;
        _boatEuler.set(0, b.dir > 0 ? 0 : Math.PI, Math.sin(ts + b.phase) * 0.04);
        _agM.compose(_agP.set(b.x, by, b.z), _agQ.setFromEuler(_boatEuler), _agS);
        boatHullInst!.setMatrixAt(i, _agM);
        boatCabinInst!.setMatrixAt(i, _agT.multiplyMatrices(_agM, _boatCabinOff));
      }
      if (boatHullInst) {
        boatHullInst.instanceMatrix.needsUpdate = true;
        boatCabinInst!.instanceMatrix.needsUpdate = true;
      }
      for (const s of steams) {
        const rise = (ts * 6 + s.phase * 5) % 20;
        s.mesh.position.y = s.baseY + rise;
        s.mesh.position.x += Math.sin(ts * 0.7 + s.phase) * 0.012;
        (s.mesh.material as THREE.MeshBasicMaterial).opacity = 0.24 * (1 - rise / 20);
        s.mesh.scale.setScalar(0.75 + rise * 0.025);
      }
      for (const s of signals) {
        const green = Math.sin(ts * 0.45 + s.phase) > 0;
        s.green.emissiveIntensity = green ? 1.8 : 0.08;
        s.red.emissiveIntensity = green ? 0.08 : 1.8;
      }
      // Auto quality: sample FPS each second and nudge the render scale to hold the target band.
      // MUST run before composer.render() below — resizing the renderer/composer clears the WebGL
      // drawing buffer immediately, and since this whole loop body is one synchronous task, the
      // browser only ever paints whatever the canvas holds when the task ends. Resizing after
      // render() meant the buffer was cleared *after* the frame was drawn, so the compositor picked
      // up a blank buffer — a one-frame black flash, roughly once a second, on the default "Auto"
      // preset. Resizing first (then rendering into the freshly-sized buffer) fixes it.
      if (gfxAdaptive) {
        gfxFrames++;
        gfxAccum += dt;
        if (gfxAccum >= 1) {
          const fps = gfxFrames / gfxAccum;
          gfxFrames = 0;
          gfxAccum = 0;
          // gfxBaseScale is 1.0 for Auto, so gfxDynScale doubles as the absolute scale here; float it
          // within [MIN, MAX] to hold the target band (down on weak HW / night, up on strong HW / day).
          let next = gfxDynScale;
          if (fps < AUTO_TARGET_LOW) next = Math.max(AUTO_MIN_SCALE, gfxDynScale - 0.1);
          else if (fps > AUTO_TARGET_HIGH && gfxDynScale < AUTO_MAX_SCALE) next = Math.min(AUTO_MAX_SCALE, gfxDynScale + 0.08);
          if (Math.abs(next - gfxDynScale) > 0.001) {
            gfxDynScale = next;
            setRenderScale(gfxBaseScale * gfxDynScale);
          }
        }
      }
      composer.render();
      // Marker/district labels only need re-projection when the camera or hover state changed
      // (a cheap numeric signature catches pan/zoom/dolly from any source). Risers animate always.
      {
        const sig = camera.position.x + camera.position.z * 3 + camera.zoom * 9973 + controls.target.x * 5 + controls.target.z * 7;
        if (labelsDirty || sig !== lastCamSig) {
          lastCamSig = sig;
          labelsDirty = false;
          projectLabels();
        }
        if (risers.length) projectRisers();
        if (buySel && buyEl) {
          // keep the buy card pinned above its plot while the camera moves
          const dom = renderer!.domElement;
          proj.set(buyAnchor.x, buyAnchor.y, buyAnchor.z).project(camera);
          buyEl.style.transform = `translate(-50%,-100%) translate(${(proj.x * 0.5 + 0.5) * dom.clientWidth}px,${(-proj.y * 0.5 + 0.5) * dom.clientHeight - 8}px)`;
        }
      }
    };
    raf = requestAnimationFrame(loop);

    let prevKey = buildKey(current);
    unsub = worldStore.subscribe((c) => {
      current = c;
      const k = buildKey(c);
      if (k !== prevKey) {
        prevKey = k;
        syncBuildings();
        return;
      }
      for (const b of c.buildings) {
        const prev = prevProd[b.id] ?? b.res.product;
        const delta = b.res.product - prev;
        if (delta > 0) made[b.id] = (made[b.id] ?? 0) + delta;
        else if (delta < 0) sold[b.id] = (sold[b.id] ?? 0) + -delta;
        prevProd[b.id] = b.res.product;
      }
      // refresh owned marker sublabels (crew/stock) — cached element, write only on change
      let mi = current.plots.length;
      for (const b of c.buildings) {
        const m = markers[mi++];
        if (m) {
          const text = subText(b);
          if (text !== m.lastSub) {
            m.lastSub = text;
            m.sub.textContent = text;
          }
        }
      }
    });
  });

  onDestroy(() => {
    destroyed = true;
    cancelAnimationFrame(raf);
    ro?.disconnect();
    unsub?.();
    gfxUnsub?.();
    if (renderer) {
      const dom = renderer.domElement;
      dom.removeEventListener('pointerdown', onDown);
      dom.removeEventListener('pointermove', onMove);
      dom.removeEventListener('pointerup', onUp);
      dom.removeEventListener('contextmenu', onCtx);
    }
    controls?.dispose();
    ringMatOwned?.dispose();
    ringMatForSale?.dispose();
    ringMatOwnedHover?.dispose();
    ringMatForSaleHover?.dispose();
    ringGeo?.dispose();
    hitGeo?.dispose();
    hitMat?.dispose();
    ownedTintGeo?.dispose();
    ownedTintMat?.dispose();
    facadeCache.forEach((t) => {
      t.color.dispose();
      t.emissive.dispose();
      t.normal.dispose();
      t.rough.dispose();
    });
    facadeCache.clear();
    composer?.dispose();
    renderer?.dispose();
    renderer?.domElement?.remove();
    renderer = undefined;
  });
</script>

<div class="cwrap" bind:this={wrap}>
  <div class="labels" bind:this={labelHost}></div>
  {#if buySel}
    <div class="buycard" bind:this={buyEl} style="transform: translate(-9999px, -9999px)">
      <div class="bc-name">{buySel.name}</div>
      <div class="bc-meta">
        {buySel.district} · {buySel.size} · 👥 {SIZE_FLOORS[buySel.size].crew} crew
      </div>
      <div class="bc-demand" title={districtMeta(buySel.district).blurb}>
        💲×{districtMeta(buySel.district).demand.toFixed(2)} demand · 🔥×{districtMeta(buySel.district).heat.toFixed(2)} heat
      </div>
      <div class="bc-actions">
        <button class="bc-buy" disabled={!buyAffordable} on:click={confirmBuy}>
          {buyAffordable ? `Buy for ${money(buySel.cost)}` : `Need ${money(buySel.cost)}`}
        </button>
        <button class="bc-cancel" on:click={() => (buySel = null)} aria-label="Cancel">✕</button>
      </div>
    </div>
  {/if}
  <div class="vignette"></div>
</div>

<style>
  .cwrap {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 320px;
    overflow: hidden;
    border-radius: 14px;
    border: 1px solid var(--line);
    background: #0b0e13;
    cursor: grab;
    touch-action: none;
  }
  .cwrap:active {
    cursor: grabbing;
  }
  .labels {
    position: absolute;
    inset: 0;
    pointer-events: none;
    overflow: hidden;
    z-index: 2;
  }
  /* Buy-confirmation card pinned above a for-sale plot. */
  .buycard {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 3;
    pointer-events: auto;
    text-align: center;
    white-space: nowrap;
    padding: 9px 12px 10px;
    border-radius: 10px;
    background: rgba(10, 13, 17, 0.92);
    border: 1px solid rgba(243, 181, 74, 0.55);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.55);
    backdrop-filter: blur(4px);
  }
  .buycard .bc-name {
    font: 700 14px 'Trebuchet MS', sans-serif;
    color: #f5f5fa;
  }
  .buycard .bc-meta {
    font: 700 10.5px 'SF Mono', monospace;
    color: #c8ccd6;
    margin: 3px 0 2px;
    text-transform: capitalize;
  }
  .buycard .bc-demand {
    font: 700 10.5px 'SF Mono', monospace;
    color: #7fd894;
    margin: 0 0 8px;
  }
  .buycard .bc-actions {
    display: flex;
    gap: 6px;
    justify-content: center;
  }
  .buycard .bc-buy {
    font: 700 12px 'Trebuchet MS', sans-serif;
    color: #08130c;
    background: #39e070;
    border: none;
    border-radius: 7px;
    padding: 6px 12px;
    cursor: pointer;
  }
  .buycard .bc-buy:hover:not(:disabled) {
    background: #5af08c;
  }
  .buycard .bc-buy:disabled {
    background: #3a4046;
    color: #9aa3ad;
    cursor: default;
  }
  .buycard .bc-cancel {
    font: 700 12px 'Trebuchet MS', sans-serif;
    color: #c8ccd6;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 7px;
    padding: 6px 9px;
    cursor: pointer;
  }
  .buycard .bc-cancel:hover {
    background: rgba(255, 255, 255, 0.16);
  }
  /* Perimeter color grade: gently darkens the edges so the eye settles on the playable core. */
  .vignette {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background: radial-gradient(125% 125% at 50% 42%, rgba(0, 0, 0, 0) 48%, rgba(20, 12, 6, 0.3) 84%, rgba(12, 7, 3, 0.55) 100%);
  }
  .labels :global(.marker) {
    position: absolute;
    top: 0;
    left: 0;
    text-align: center;
    white-space: nowrap;
    text-shadow: 0 1px 3px #000, 0 0 6px #000;
    padding: 4px 8px;
    border-radius: 7px;
    background: rgba(10, 13, 17, 0.62);
    border: 1px solid rgba(255, 255, 255, 0.09);
    opacity: 0.72;
    backdrop-filter: blur(3px);
    transition: opacity 0.18s ease, visibility 0.18s;
    pointer-events: auto; /* labels are click targets (enter / buy card) */
    cursor: pointer;
  }
  .labels :global(.marker:hover) {
    opacity: 1;
    border-color: rgba(255, 255, 255, 0.42);
  }
  /* faded out by the label projector (crowding / zoom) — transitions instead of display pops */
  .labels :global(.marker.mhidden) {
    opacity: 0 !important;
    visibility: hidden;
  }
  .labels :global(.marker.affordable),
  .labels :global(.marker.owned) {
    opacity: 1;
  }
  .labels :global(.marker.affordable) {
    border-color: rgba(243, 181, 74, 0.62);
    box-shadow: 0 0 14px rgba(243, 181, 74, 0.2);
  }
  .labels :global(.marker.owned) {
    border-color: rgba(57, 224, 112, 0.48);
    background: rgba(8, 24, 15, 0.74);
  }
  .labels :global(.marker.active) {
    box-shadow: 0 0 18px rgba(57, 224, 112, 0.36);
  }
  .labels :global(.marker.hover) {
    opacity: 1;
    border-color: rgba(255, 255, 255, 0.42);
    box-shadow: 0 0 16px rgba(0, 0, 0, 0.6);
  }
  .labels :global(.marker .mname) {
    font: 700 13px 'Trebuchet MS', sans-serif;
    color: #f5f5fa;
  }
  .labels :global(.marker.owned .mname) {
    color: #c9f7d8;
  }
  .labels :global(.marker .msub) {
    font: 700 10.5px 'SF Mono', monospace;
    color: #ffcf8a;
  }
  .labels :global(.marker.owned .msub) {
    color: #b6e8c6;
  }
  .labels :global(.district) {
    position: absolute;
    top: 0;
    left: 0;
    transform: translate(-50%, -50%);
    font: 700 14px 'Trebuchet MS', sans-serif;
    letter-spacing: 3px;
    color: #f3ead6;
    opacity: 0.62;
    text-shadow: 0 1px 4px #000;
    white-space: nowrap;
  }
  .labels :global(.district.d0) { color: #d4e8ff; }
  .labels :global(.district.d1) { color: #ffd1b2; }
  .labels :global(.district.d2) { color: #e0ccff; }
  .labels :global(.district.d3) { color: #bfe8dd; }
  .labels :global(.district.d4) { color: #ffe0a8; }
  .labels :global(.district.d5) { color: #efc1b0; }
  .labels :global(.district.d6) { color: #ffc1df; }
  .labels :global(.district.d7) { color: #b6e5f0; }
  .labels :global(.riser) {
    position: absolute;
    top: 0;
    left: 0;
    font: 700 12px 'SF Mono', monospace;
    padding: 2px 6px;
    border-radius: 5px;
    background: rgba(13, 18, 24, 0.82);
    white-space: nowrap;
  }
  .labels :global(.riser.cash) {
    color: #59d98a;
    border: 1px solid rgba(89, 217, 138, 0.6);
  }
  .labels :global(.riser.grow) {
    color: #47d36a;
    border: 1px solid rgba(71, 211, 106, 0.6);
  }
</style>
